import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  VoiceSessionCommandRequest,
  VoiceSessionSnapshot,
} from '../client';
import { useApi } from '../state/ApiContext';
import { VoicePlayback } from './media/VoicePlayback';
import { VoiceRecorder } from './media/VoiceRecorder';
import {
  OpenAIRealtimeTransport,
  OpenAIRealtimeTransportError,
  type OpenAIRealtimeTransportLike,
  type OpenAIRealtimeTransportOptions,
} from './transports/OpenAIRealtimeTransport';
import {
  VoiceSocket,
  type VoiceSocketLike,
  type VoiceSocketOptions,
} from './voice-socket';
import type {
  NormalizedVoiceEvent,
  ProvisionalVoiceTranscript,
  VoiceChannelStatus,
  VoiceMediaAuthority,
  VoiceModelState,
} from './voice-types';

const INPUT_SAMPLE_RATE_HZ = 16_000;
const DEVICE_STORAGE_KEY = 'voice.devices.v1';
const MAX_CRITICAL_CLEANUP_ATTEMPTS = 3;
const CRITICAL_CLEANUP_ATTEMPT_TIMEOUT_MS = 2_000;
const MAX_DEFERRED_STALE_SESSIONS = 16;
const FINISHING_PREVIOUS_SESSION_MESSAGE =
  'Finishing previous voice session; retry shortly';
const MAX_REMOTE_AUTHORITY_SCOPE_SESSIONS = MAX_DEFERRED_STALE_SESSIONS + 1;
const ECHO_CANCELLATION_WARNING =
  'Echo cancellation is off. Use headphones to prevent feedback.';

interface RecorderLike {
  start(deviceId?: string): Promise<void>;
  stop(): Promise<void>;
}

interface PlaybackLike {
  resume(deviceId?: string): Promise<void>;
  enqueue(pcm: Uint8Array, sampleRateHz: number): void;
  complete(): void;
  interrupt(): void;
  close(): Promise<void>;
}

interface PreparedPlayback {
  playback: PlaybackLike;
  generation: number;
  ready: Promise<void>;
  outputElement: HTMLAudioElement;
  prearmedContext?: AudioContext;
  inputDeviceId?: string;
  outputDeviceId?: string;
}

export interface VoiceChannelDependencies {
  randomUUID: () => string;
  createRecorder: (options: {
    sampleRateHz: number;
    onChunk: (dataBase64: string) => void;
    onLevel: (level: number) => void;
    onSpectrum: (spectrum: number[]) => void;
  }) => RecorderLike;
  createPlayback: (options: {
    onLevel: (level: number) => void;
    onSpectrum: (spectrum: number[]) => void;
    outputElement?: HTMLAudioElement;
  }) => PlaybackLike;
  createSocket: (options: VoiceSocketOptions) => VoiceSocketLike;
  createOpenAITransport?: (
    options: OpenAIRealtimeTransportOptions,
  ) => OpenAIRealtimeTransportLike;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createAudioElement?: () => HTMLAudioElement;
  createAudioContext?: () => AudioContext;
  storage?: Storage;
}

export interface OpenVoiceChannelInput {
  teamId: number;
  agentId: number;
  inputDeviceId?: string;
  outputDeviceId?: string;
}

interface VoiceChannelCoreValue {
  activeConversationId: number | null;
  session: VoiceSessionSnapshot | null;
  status: VoiceChannelStatus;
  /** Monotonic count of accepted provider usage observations. */
  providerUsageObservationGeneration: number;
  modelState: VoiceModelState | null;
  /** True while the user's microphone is gated; the model cannot hear them. */
  muted: boolean;
  /** Timestamp of the most recent user barge-in over agent speech. */
  bargeInAt: number | null;
  inputLevel: number;
  outputLevel: number;
  inputSpectrum: number[] | null;
  outputSpectrum: number[] | null;
  provisionalTranscripts: Readonly<Record<string, ProvisionalVoiceTranscript>>;
  mediaWarning: string | null;
  error: string | null;
  setMuted: (muted: boolean) => void;
  openForConversation: (
    conversationId: number,
    input: OpenVoiceChannelInput,
  ) => Promise<void>;
  pause: (conversationId?: number) => Promise<void>;
  resume: (conversationId?: number) => Promise<void>;
  switchAgent: (agentId: number, conversationId?: number) => Promise<void>;
  beginPtt: (conversationId?: number) => Promise<void>;
  commitPtt: (conversationId?: number) => Promise<void>;
  cancelPtt: (conversationId?: number) => Promise<void>;
  interrupt: (conversationId?: number) => Promise<void>;
  end: (conversationId?: number) => Promise<void>;
  returnToText: (conversationId?: number) => Promise<void>;
}

export interface VoiceChannelValue extends Omit<
  VoiceChannelCoreValue,
  'openForConversation'
> {
  conversationId: number;
  isCurrentConversation: boolean;
  open: (input: OpenVoiceChannelInput) => Promise<void>;
}

export const VoiceChannelContext = createContext<VoiceChannelCoreValue | null>(
  null,
);

const defaultDependencies = (): VoiceChannelDependencies => ({
  randomUUID: () => crypto.randomUUID(),
  createRecorder: (options) => new VoiceRecorder(options),
  createPlayback: (options) =>
    new VoicePlayback({
      onLevel: options.onLevel,
      onSpectrum: options.onSpectrum,
      ...(options.outputElement
        ? { createAudioElement: () => options.outputElement! }
        : {}),
    }),
  createSocket: (options) => new VoiceSocket(options),
  createOpenAITransport: (options) => new OpenAIRealtimeTransport(options),
  getUserMedia: (constraints) =>
    navigator.mediaDevices.getUserMedia(constraints),
  createAudioElement: () => new Audio(),
  storage: safeSessionStorage(),
});

const safeSessionStorage = (): Storage | undefined => {
  try {
    return window.sessionStorage;
  } catch (_error: unknown) {
    return undefined;
  }
};

type VoiceDevices = { input?: string; output?: string };

function realtimeMicrophoneConstraints(
  inputDeviceId?: string,
): MediaTrackConstraints {
  return {
    ...(inputDeviceId ? { deviceId: { exact: inputDeviceId } } : {}),
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  };
}

function hardenRealtimeMicrophoneStream(stream: MediaStream): string | null {
  let echoCancellationDisabled = false;
  for (const track of stream.getAudioTracks()) {
    if ('contentHint' in track) {
      try {
        track.contentHint = 'speech';
      } catch {}
    }
    try {
      if (track.getSettings().echoCancellation === false) {
        echoCancellationDisabled = true;
      }
    } catch {}
  }
  return echoCancellationDisabled ? ECHO_CANCELLATION_WARNING : null;
}

interface VoiceStartAuthority {
  teamId: number;
  conversationId: number;
  agentId: number;
}

interface VoiceOpenAuthority extends VoiceStartAuthority {
  lifecycleGeneration: number;
  openRequest: number;
}

interface VoiceSessionClaim {
  key: string;
  lifecycleGeneration: number;
  openRequest: number;
}

interface VoiceOpenAllocation {
  lifecycleGeneration: number;
  openRequest: number;
  localRelease: Promise<PromiseSettledResult<void>>;
  remoteRelease: Promise<PromiseSettledResult<void>>;
  outputElement: HTMLAudioElement | null;
  preparedPlayback: PreparedPlayback | null;
  cancelled: Promise<void>;
  cancel: () => void;
  disposed: boolean;
}

type StaleResponseOrigin =
  { kind: 'start'; authority: VoiceStartAuthority } | { kind: 'command' };

interface DeferredStaleSession {
  session: VoiceSessionSnapshot;
  origin: StaleResponseOrigin;
}

type SessionSource = 'local' | 'remote';

const voiceSessionKey = (value: VoiceSessionSnapshot): string =>
  `${value.sessionId}:${value.leaseVersion}`;

const sameStartAuthority = (
  left: VoiceStartAuthority,
  right: VoiceStartAuthority,
): boolean =>
  left.teamId === right.teamId &&
  left.conversationId === right.conversationId &&
  left.agentId === right.agentId;

const profileIdentity = (session: VoiceSessionSnapshot): string =>
  JSON.stringify(session.profileSnapshot);

const mediaAuthority = (
  session: VoiceSessionSnapshot | null,
): VoiceMediaAuthority | null => {
  if (!session) return null;
  if (
    typeof session.mediaBindingId !== 'string' ||
    session.mediaBindingId.length === 0 ||
    !Number.isSafeInteger(session.mediaGeneration) ||
    session.mediaGeneration < 0
  ) {
    return null;
  }
  return {
    sessionId: session.sessionId,
    leaseVersion: session.leaseVersion,
    mediaBindingId: session.mediaBindingId,
    mediaGeneration: session.mediaGeneration,
  };
};

const requireMediaAuthority = (
  session: VoiceSessionSnapshot,
): VoiceMediaAuthority => {
  const authority = mediaAuthority(session);
  if (!authority) {
    throw new Error('OpenAI Realtime media authority is unavailable');
  }
  return authority;
};

const sameMediaBindingGeneration = (
  left: VoiceMediaAuthority | null,
  right: VoiceMediaAuthority | null,
): boolean =>
  left !== null &&
  right !== null &&
  left.mediaBindingId === right.mediaBindingId &&
  left.mediaGeneration === right.mediaGeneration;

const sameMediaAuthority = (
  left: VoiceMediaAuthority | null,
  right: VoiceMediaAuthority | null,
): boolean =>
  sameMediaBindingGeneration(left, right) &&
  left!.sessionId === right!.sessionId &&
  left!.leaseVersion === right!.leaseVersion;

const sameSessionMediaBindingGeneration = (
  left: VoiceMediaAuthority | null,
  right: VoiceMediaAuthority | null,
): boolean =>
  sameMediaBindingGeneration(left, right) &&
  left!.sessionId === right!.sessionId;

const isNewMediaBindingGeneration = (
  current: VoiceMediaAuthority | null,
  candidate: VoiceMediaAuthority | null,
): boolean =>
  current !== null &&
  candidate !== null &&
  current.sessionId === candidate.sessionId &&
  candidate.mediaGeneration > current.mediaGeneration &&
  candidate.mediaBindingId !== current.mediaBindingId;

const rejectsMediaAuthorityTransition = (
  current: VoiceSessionSnapshot,
  candidate: VoiceSessionSnapshot,
): boolean => {
  if (
    current.sessionId !== candidate.sessionId ||
    current.transport.type !== 'webrtc' ||
    candidate.transport.type !== 'webrtc' ||
    !['active', 'reconnecting'].includes(current.state) ||
    !['active', 'reconnecting'].includes(candidate.state)
  ) {
    return false;
  }
  const currentAuthority = mediaAuthority(current);
  const candidateAuthority = mediaAuthority(candidate);
  if (!currentAuthority || !candidateAuthority) return true;
  if (candidateAuthority.mediaGeneration < currentAuthority.mediaGeneration) {
    return true;
  }
  if (candidateAuthority.mediaGeneration === currentAuthority.mediaGeneration) {
    return (
      candidateAuthority.mediaBindingId !== currentAuthority.mediaBindingId
    );
  }
  return candidateAuthority.mediaBindingId === currentAuthority.mediaBindingId;
};

const snapshotAuthorityIdentity = (session: VoiceSessionSnapshot): string =>
  JSON.stringify({
    sessionId: session.sessionId,
    teamId: session.teamId,
    conversationId: session.conversationId,
    userId: session.userId,
    currentAgentId: session.currentAgentId,
    profileSnapshot: session.profileSnapshot,
    transport: session.transport,
    state: session.state,
    leaseActive: session.leaseActive,
    capabilities: session.capabilities,
    terminalReason: session.terminalReason,
    mediaAuthority: mediaAuthority(session),
  });

const sameSnapshotAuthority = (
  left: VoiceSessionSnapshot,
  right: VoiceSessionSnapshot,
): boolean =>
  snapshotAuthorityIdentity(left) === snapshotAuthorityIdentity(right);

const mediaTopologyIdentity = (session: VoiceSessionSnapshot): string =>
  JSON.stringify({
    currentAgentId: session.currentAgentId,
    profileSnapshot: session.profileSnapshot,
    transport: session.transport,
    mediaBindingGeneration: (() => {
      const authority = mediaAuthority(session);
      return authority
        ? [authority.mediaBindingId, authority.mediaGeneration]
        : null;
    })(),
  });

const sameMediaTopology = (
  left: VoiceSessionSnapshot,
  right: VoiceSessionSnapshot,
): boolean => mediaTopologyIdentity(left) === mediaTopologyIdentity(right);

const isCurrentMediaTopology = (
  current: VoiceSessionSnapshot | null,
  candidate: VoiceSessionSnapshot,
): boolean =>
  (current?.state === 'active' || current?.state === 'reconnecting') &&
  current.sessionId === candidate.sessionId &&
  sameMediaTopology(current, candidate);

const isCurrentAttachTopology = (
  current: VoiceSessionSnapshot | null,
  candidate: VoiceSessionSnapshot,
): boolean =>
  current?.state === 'active' && isCurrentMediaTopology(current, candidate);

const hasActiveLease = (session: VoiceSessionSnapshot): boolean =>
  session.state === 'active' && session.leaseActive;

const sessionMatchesStartAuthority = (
  session: VoiceSessionSnapshot,
  authority: VoiceStartAuthority,
): boolean =>
  session.teamId === authority.teamId &&
  session.conversationId === authority.conversationId &&
  session.currentAgentId === authority.agentId;

const sessionMatchesSwitchAuthority = (
  current: VoiceSessionSnapshot,
  candidate: VoiceSessionSnapshot,
  requestedAgentId: number,
): boolean =>
  candidate.sessionId === current.sessionId &&
  candidate.teamId === current.teamId &&
  candidate.conversationId === current.conversationId &&
  candidate.userId === current.userId &&
  candidate.currentAgentId === requestedAgentId &&
  candidate.leaseVersion >= current.leaseVersion;

const compatibleStartRenewal = (
  candidate: DeferredStaleSession,
  currentAuthority: VoiceStartAuthority,
  currentSession: VoiceSessionSnapshot,
): boolean =>
  candidate.origin.kind === 'start' &&
  hasActiveLease(candidate.session) &&
  sameStartAuthority(candidate.origin.authority, currentAuthority) &&
  candidate.session.sessionId === currentSession.sessionId &&
  sessionMatchesStartAuthority(candidate.session, currentAuthority) &&
  sessionMatchesStartAuthority(currentSession, currentAuthority) &&
  candidate.session.userId === currentSession.userId &&
  candidate.session.transport.type === currentSession.transport.type &&
  candidate.session.transport.websocketPath ===
    currentSession.transport.websocketPath &&
  profileIdentity(candidate.session) === profileIdentity(currentSession);

const deferredCandidateKey = (candidate: DeferredStaleSession): string =>
  JSON.stringify([
    candidate.origin.kind,
    candidate.origin.kind === 'start' ? candidate.origin.authority : null,
    snapshotAuthorityIdentity(candidate.session),
  ]);

const readDevices = (storage: Storage | undefined): VoiceDevices => {
  try {
    const value = storage?.getItem(DEVICE_STORAGE_KEY);
    if (!value) return {};
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return {};
    const devices = parsed as Record<string, unknown>;
    return {
      ...(typeof devices.input === 'string' ? { input: devices.input } : {}),
      ...(typeof devices.output === 'string' ? { output: devices.output } : {}),
    };
  } catch (_error: unknown) {
    return {};
  }
};

const writeDevices = (
  storage: Storage | undefined,
  devices: VoiceDevices,
): void => {
  try {
    if (devices.input || devices.output) {
      storage?.setItem(DEVICE_STORAGE_KEY, JSON.stringify(devices));
    } else {
      storage?.removeItem(DEVICE_STORAGE_KEY);
    }
  } catch (_error: unknown) {
    return;
  }
};

const decodeBase64 = (value: string): Uint8Array => {
  const decoded = window.atob(value);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
};

const voiceRequestErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') return null;
  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== 'object') return null;
  const data = (response as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
};

/**
 * Turns a rejected offer request into the transport's own error type when the
 * server named a cause the person on the call can act on. The transport
 * otherwise reports every negotiation rejection as a bare failure, which
 * cannot tell "the provider account is out of credit" - an operator's problem,
 * and permanent until they fix it - from a transient one.
 */
const negotiationTransportError = (error: unknown): unknown =>
  voiceRequestErrorCode(error) === 'voice_provider_quota_exhausted'
    ? new OpenAIRealtimeTransportError(
        'provider_quota_exhausted',
        false,
        'The voice provider account has no remaining credit',
      )
    : error;

const isTerminalVoiceAuthorityError = (error: unknown): boolean =>
  ['voice_session_not_found', 'voice_session_ended'].includes(
    voiceRequestErrorCode(error) ?? '',
  );

interface RemoteVoiceAuthorityEntry {
  key: string;
  generation: number;
  session: VoiceSessionSnapshot;
  body: VoiceSessionCommandRequest;
  attempts: number;
  state: 'pending' | 'exhausted';
  promise: Promise<VoiceSessionSnapshot>;
  resolve: (snapshot: VoiceSessionSnapshot) => void;
  reject: (reason: unknown) => void;
  callerSettled: boolean;
  timers: Set<ReturnType<typeof setTimeout>>;
}

interface RemoteVoiceAuthorityScope {
  teamId: number;
  conversationId: number;
  userId: number;
}

const remoteAuthorityScope = (
  session: VoiceSessionSnapshot,
): RemoteVoiceAuthorityScope => ({
  teamId: session.teamId,
  conversationId: session.conversationId,
  userId: session.userId,
});

const remoteAuthorityScopeKey = (scope: RemoteVoiceAuthorityScope): string =>
  `${scope.teamId}:${scope.conversationId}:${scope.userId}`;

const remoteAuthorityBodySignature = (
  body: VoiceSessionCommandRequest,
): string => {
  const semanticBody = Object.entries(body)
    .filter(([key]) => key !== 'commandId')
    .sort(([left], [right]) => left.localeCompare(right));
  return JSON.stringify(semanticBody);
};

export class RemoteVoiceAuthorityRegistry {
  private readonly entries = new Map<string, RemoteVoiceAuthorityEntry>();
  private nextGeneration = 0;

  constructor(
    private readonly dispatch: (
      sessionId: string,
      body: VoiceSessionCommandRequest,
    ) => unknown,
  ) {}

  hasUncertainty(): boolean {
    return this.entries.size > 0;
  }

  hasOnlyExhaustedUncertainty(): boolean {
    return (
      this.entries.size > 0 &&
      [...this.entries.values()].every((entry) => entry.state === 'exhausted')
    );
  }

  scopes(conversationId?: number): RemoteVoiceAuthorityScope[] {
    const scopes = new Map<string, RemoteVoiceAuthorityScope>();
    for (const entry of this.entries.values()) {
      const scope = remoteAuthorityScope(entry.session);
      if (
        conversationId !== undefined &&
        scope.conversationId !== conversationId
      ) {
        continue;
      }
      scopes.set(remoteAuthorityScopeKey(scope), scope);
    }
    return [...scopes.values()].sort((left, right) =>
      remoteAuthorityScopeKey(left).localeCompare(
        remoteAuthorityScopeKey(right),
      ),
    );
  }

  async endAll(
    scope: RemoteVoiceAuthorityScope,
    candidates: readonly VoiceSessionSnapshot[],
    createCommandId: () => string,
  ): Promise<void> {
    const scopeKey = remoteAuthorityScopeKey(scope);
    const sessions = new Map<string, VoiceSessionSnapshot>();
    for (const entry of this.entries.values()) {
      if (
        remoteAuthorityScopeKey(remoteAuthorityScope(entry.session)) ===
        scopeKey
      ) {
        sessions.set(voiceSessionKey(entry.session), entry.session);
      }
    }
    for (const candidate of candidates) {
      if (
        remoteAuthorityScopeKey(remoteAuthorityScope(candidate)) === scopeKey
      ) {
        sessions.set(voiceSessionKey(candidate), candidate);
      }
    }
    const ordered = [...sessions.values()].sort((left, right) =>
      voiceSessionKey(left).localeCompare(voiceSessionKey(right)),
    );
    if (ordered.length > MAX_REMOTE_AUTHORITY_SCOPE_SESSIONS) {
      throw new Error('Voice remote-authority scope capacity was exceeded');
    }
    await Promise.all(
      ordered
        .filter((session) => session.state !== 'ended')
        .map((session) =>
          this.execute(
            session,
            {
              commandId: createCommandId(),
              leaseVersion: session.leaseVersion,
              type: 'end',
            },
            { rearmExhaustedEnd: true },
          ),
        ),
    );
  }

  execute(
    session: VoiceSessionSnapshot,
    body: VoiceSessionCommandRequest,
    options: { rearmExhaustedEnd?: boolean } = {},
  ): Promise<VoiceSessionSnapshot> {
    const key = `${voiceSessionKey(session)}:${remoteAuthorityBodySignature(body)}`;
    const existing = this.entries.get(key);
    if (
      existing &&
      !(
        options.rearmExhaustedEnd === true &&
        body.type === 'end' &&
        existing.state === 'exhausted'
      )
    ) {
      return existing.promise;
    }
    let resolve = (_snapshot: VoiceSessionSnapshot): void => undefined;
    let reject = (_reason: unknown): void => undefined;
    const promise = new Promise<VoiceSessionSnapshot>((onResolve, onReject) => {
      resolve = onResolve;
      reject = onReject;
    });
    const entry: RemoteVoiceAuthorityEntry = {
      key,
      generation: ++this.nextGeneration,
      session,
      body,
      attempts: 0,
      state: 'pending',
      promise,
      resolve,
      reject,
      callerSettled: false,
      timers: new Set(),
    };
    this.entries.set(key, entry);
    this.attempt(entry);
    return promise;
  }

  private attempt(entry: RemoteVoiceAuthorityEntry): void {
    if (this.entries.get(entry.key) !== entry) return;
    entry.attempts += 1;
    let attemptFinished = false;
    const timer = setTimeout(() => {
      entry.timers.delete(timer);
      if (this.entries.get(entry.key) !== entry) return;
      attemptFinished = true;
      this.retryOrExhaust(
        entry,
        new Error('Voice authority command timed out'),
      );
    }, CRITICAL_CLEANUP_ATTEMPT_TIMEOUT_MS);
    entry.timers.add(timer);
    let request: Promise<unknown>;
    try {
      request = Promise.resolve(
        this.dispatch(entry.session.sessionId, entry.body),
      );
    } catch (error: unknown) {
      request = Promise.reject(error);
    }
    void request.then(
      (response: unknown) => {
        const authoritativeEntry = this.entries.get(entry.key);
        if (authoritativeEntry !== entry) {
          if (
            entry.body.type === 'end' &&
            authoritativeEntry &&
            authoritativeEntry.generation > entry.generation &&
            voiceSessionKey(authoritativeEntry.session) ===
              voiceSessionKey(entry.session)
          ) {
            const snapshot = (response as { data: VoiceSessionSnapshot }).data;
            this.reconcileSession(entry.session.sessionId, snapshot);
          }
          return;
        }
        clearTimeout(timer);
        entry.timers.delete(timer);
        const snapshot = (response as { data: VoiceSessionSnapshot }).data;
        if (entry.body.type === 'end') {
          this.reconcileSession(entry.session.sessionId, snapshot);
        } else {
          this.settleEntry(entry, snapshot);
        }
      },
      (error: unknown) => {
        if (this.entries.get(entry.key) !== entry) return;
        if (isTerminalVoiceAuthorityError(error)) {
          this.reconcileSession(entry.session.sessionId, {
            ...entry.session,
            state: 'ended',
            leaseActive: false,
          });
          return;
        }
        if (attemptFinished) return;
        attemptFinished = true;
        clearTimeout(timer);
        entry.timers.delete(timer);
        this.retryOrExhaust(entry, error);
      },
    );
  }

  private retryOrExhaust(
    entry: RemoteVoiceAuthorityEntry,
    reason: unknown,
  ): void {
    if (entry.attempts < MAX_CRITICAL_CLEANUP_ATTEMPTS) {
      this.attempt(entry);
      return;
    }
    entry.state = 'exhausted';
    if (!entry.callerSettled) {
      entry.callerSettled = true;
      entry.reject(reason);
    }
    if (entry.body.type === 'end') {
      console.warn('Voice remote-authority retries were exhausted');
    }
  }

  private settleEntry(
    entry: RemoteVoiceAuthorityEntry,
    snapshot: VoiceSessionSnapshot,
  ): void {
    if (this.entries.get(entry.key) !== entry) return;
    this.entries.delete(entry.key);
    for (const timer of entry.timers) clearTimeout(timer);
    entry.timers.clear();
    if (!entry.callerSettled) {
      entry.callerSettled = true;
      entry.resolve(snapshot);
    }
  }

  private reconcileSession(
    sessionId: string,
    snapshot: VoiceSessionSnapshot,
  ): void {
    for (const entry of [...this.entries.values()]) {
      if (entry.session.sessionId !== sessionId) continue;
      this.entries.delete(entry.key);
      for (const timer of entry.timers) clearTimeout(timer);
      entry.timers.clear();
      if (!entry.callerSettled) {
        entry.callerSettled = true;
        entry.resolve(snapshot);
      }
    }
  }
}

export const VoiceChannelProvider: React.FC<{
  children: React.ReactNode;
  dependencies?: VoiceChannelDependencies;
}> = ({ children, dependencies: dependencyOverrides }) => {
  const { defaultApi } = useApi();
  const dependencies = useMemo(
    () => dependencyOverrides ?? defaultDependencies(),
    [dependencyOverrides],
  );
  const [activeConversationId, setActiveConversationId] = useState<
    number | null
  >(null);
  const [session, setSession] = useState<VoiceSessionSnapshot | null>(null);
  const [status, setStatus] = useState<VoiceChannelStatus>('idle');
  const [
    providerUsageObservationGeneration,
    setProviderUsageObservationGeneration,
  ] = useState(0);
  const [modelState, setModelState] = useState<VoiceModelState | null>(null);
  const [muted, setMutedState] = useState(false);
  const mutedRef = useRef(false);
  const [bargeInAt, setBargeInAt] = useState<number | null>(null);
  const [inputLevel, setInputLevel] = useState(0);
  const [outputLevel, setOutputLevel] = useState(0);
  const [inputSpectrum, setInputSpectrum] = useState<number[] | null>(null);
  const [outputSpectrum, setOutputSpectrum] = useState<number[] | null>(null);
  const [provisionalTranscripts, setProvisionalTranscripts] = useState<
    Record<string, ProvisionalVoiceTranscript>
  >({});
  const [mediaWarning, setMediaWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<VoiceSessionSnapshot | null>(null);
  const conversationRef = useRef<number | null>(null);
  const recorderRef = useRef<RecorderLike | null>(null);
  const playbackRef = useRef<PlaybackLike | null>(null);
  const openAITransportRef = useRef<OpenAIRealtimeTransportLike | null>(null);
  const openAITransportPausedRef = useRef(false);
  const mediaReadyRef = useRef(false);
  const mediaAttachedRef = useRef(false);
  const socketReadyRef = useRef(false);
  const gatedOpeningAuthorityRef = useRef<VoiceMediaAuthority | null>(null);
  const socketRef = useRef<VoiceSocketLike | null>(null);
  const pttTurnRef = useRef<string | null>(null);
  const operationRef = useRef<Promise<void>>(Promise.resolve());
  const disposedRef = useRef(false);
  const ownershipGenerationRef = useRef(0);
  const lifecycleGenerationRef = useRef(0);
  const latestOpenRequestRef = useRef(0);
  const explicitEndCleanupGenerationRef = useRef<number | null>(null);
  const remoteAuthorityDispatchRef = useRef(
    (sessionId: string, body: VoiceSessionCommandRequest) =>
      defaultApi.commandVoiceSession(sessionId, body),
  );
  remoteAuthorityDispatchRef.current = (sessionId, body) =>
    defaultApi.commandVoiceSession(sessionId, body);
  const remoteAuthorityRegistryRef =
    useRef<RemoteVoiceAuthorityRegistry | null>(null);
  if (!remoteAuthorityRegistryRef.current) {
    remoteAuthorityRegistryRef.current = new RemoteVoiceAuthorityRegistry(
      (sessionId, body) => remoteAuthorityDispatchRef.current(sessionId, body),
    );
  }
  const remoteAuthorityRegistry = remoteAuthorityRegistryRef.current;
  const activeOpenAuthorityRef = useRef<VoiceOpenAuthority | null>(null);
  const sessionClaimRef = useRef<VoiceSessionClaim | null>(null);
  const deferredStaleSessionsRef = useRef<Map<string, DeferredStaleSession>>(
    new Map(),
  );
  const reconfigureMediaRef = useRef<
    (next: VoiceSessionSnapshot, lifecycleGeneration: number) => Promise<void>
  >(async () => undefined);
  const recoverColdRuntimeRef = useRef<
    (socket: VoiceSocketLike, generation: number) => Promise<void>
  >(async () => undefined);
  const coldRecoverySocketsRef = useRef<WeakSet<VoiceSocketLike>>(
    new WeakSet(),
  );
  const warmSocketRebindGenerationRef = useRef<number | null>(null);
  const preparedPlaybackRef = useRef<PreparedPlayback | null>(null);
  const openingAllocationRef = useRef<VoiceOpenAllocation | null>(null);
  const retryOpenRef = useRef<
    (conversationId: number, input: OpenVoiceChannelInput) => Promise<void>
  >(() => Promise.reject(new Error('Voice channel is not ready')));
  const topologyTransitionRef = useRef(false);
  const transitionSnapshotSocketRef = useRef<VoiceSocketLike | null>(null);
  const topologyTransitionCleanupRef = useRef<Promise<void>>(Promise.resolve());
  const topologyTransitionCompletionRef = useRef<Promise<void>>(
    Promise.resolve(),
  );

  const clearInputAnalysis = useCallback((): void => {
    setInputLevel(0);
    setInputSpectrum(null);
  }, []);

  const clearOutputAnalysis = useCallback((): void => {
    setOutputLevel(0);
    setOutputSpectrum(null);
  }, []);

  const isLifecycleCurrent = useCallback(
    (generation: number): boolean =>
      !disposedRef.current && lifecycleGenerationRef.current === generation,
    [],
  );

  const serialize = useCallback(
    <Result,>(operation: () => Promise<Result>): Promise<Result> => {
      const result = operationRef.current.then(operation, operation);
      operationRef.current = result.then(
        () => undefined,
        () => undefined,
      );
      return result;
    },
    [],
  );

  const claimSession = useCallback(
    (
      next: VoiceSessionSnapshot,
      lifecycleGeneration: number,
      openRequest: number,
    ): string | null => {
      if (next.state === 'ended') {
        sessionClaimRef.current = null;
        return null;
      }
      const key = voiceSessionKey(next);
      sessionClaimRef.current = { key, lifecycleGeneration, openRequest };
      return key;
    },
    [],
  );

  const endUnclaimedSession = useCallback(
    (staleSession: VoiceSessionSnapshot): void => {
      if (staleSession.state === 'ended' || !staleSession.leaseActive) return;
      const key = voiceSessionKey(staleSession);
      if (sessionClaimRef.current?.key === key) return;
      void remoteAuthorityRegistry
        .execute(staleSession, {
          commandId: dependencies.randomUUID(),
          leaseVersion: staleSession.leaseVersion,
          type: 'end',
        })
        .catch(() => undefined);
    },
    [dependencies, remoteAuthorityRegistry],
  );

  const endCriticalSession = useCallback(
    (criticalSession: VoiceSessionSnapshot): void => {
      void remoteAuthorityRegistry
        .execute(criticalSession, {
          commandId: dependencies.randomUUID(),
          leaseVersion: criticalSession.leaseVersion,
          type: 'end',
        })
        .catch(() => undefined);
    },
    [dependencies, remoteAuthorityRegistry],
  );

  const criticalCleanupBlocker = useCallback((): Error | null => {
    if (!remoteAuthorityRegistry.hasUncertainty()) return null;
    if (!disposedRef.current) {
      setStatus('error');
      setError(FINISHING_PREVIOUS_SESSION_MESSAGE);
    }
    return new Error(FINISHING_PREVIOUS_SESSION_MESSAGE);
  }, [remoteAuthorityRegistry]);

  const retryCriticalCleanup = useCallback(async (): Promise<void> => {
    await Promise.all(
      remoteAuthorityRegistry
        .scopes()
        .map((scope) =>
          remoteAuthorityRegistry.endAll(scope, [], dependencies.randomUUID),
        ),
    );
  }, [dependencies, remoteAuthorityRegistry]);

  const flushDeferredStaleSessions = useCallback(
    (claimKey: string | null): void => {
      const deferred = [...deferredStaleSessionsRef.current.entries()];
      deferredStaleSessionsRef.current.clear();
      for (const [, candidate] of deferred) {
        if (voiceSessionKey(candidate.session) !== claimKey) {
          endUnclaimedSession(candidate.session);
        }
      }
    },
    [endUnclaimedSession],
  );

  const cancelPtt = useCallback(async (publish = true): Promise<void> => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    pttTurnRef.current = null;
    if (publish && !disposedRef.current) clearInputAnalysis();
    if (recorder) await recorder.stop();
  }, []);

  const disposeOpeningAllocation = useCallback(
    (preserved?: VoiceOpenAllocation): Promise<void> => {
      const allocation = openingAllocationRef.current;
      if (!allocation || allocation === preserved || allocation.disposed) {
        return Promise.resolve();
      }
      openingAllocationRef.current = null;
      allocation.disposed = true;
      allocation.cancel();
      if (preparedPlaybackRef.current === allocation.preparedPlayback) {
        preparedPlaybackRef.current = null;
      }
      if (
        allocation.preparedPlayback &&
        playbackRef.current === allocation.preparedPlayback.playback
      ) {
        playbackRef.current = null;
      }
      const attempts: Array<() => unknown> = [];
      const outputElement = allocation.outputElement;
      if (outputElement) {
        attempts.push(() => outputElement.pause());
        attempts.push(() => {
          outputElement.srcObject = null;
        });
        attempts.push(() => outputElement.removeAttribute('src'));
      }
      const preparedPlayback = allocation.preparedPlayback;
      if (preparedPlayback) {
        attempts.push(() => preparedPlayback.playback.interrupt());
        attempts.push(() => preparedPlayback.playback.close());
        if (
          preparedPlayback.prearmedContext &&
          preparedPlayback.prearmedContext.state !== 'closed'
        ) {
          attempts.push(() => preparedPlayback.prearmedContext!.close());
        }
      }
      const results = attempts.map((attempt) => {
        try {
          return Promise.resolve(attempt());
        } catch (cleanupError: unknown) {
          return Promise.reject(cleanupError);
        }
      });
      return Promise.allSettled(results).then((settled) => {
        const failure = settled.find(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected',
        );
        if (failure) throw failure.reason;
      });
    },
    [],
  );

  const releaseMedia = useCallback(
    async (
      publish = true,
      preserveTransition = false,
      preservedOpeningAllocation?: VoiceOpenAllocation,
    ): Promise<void> => {
      mediaReadyRef.current = false;
      mediaAttachedRef.current = false;
      socketReadyRef.current = false;
      const openingAllocationRelease = disposeOpeningAllocation(
        preservedOpeningAllocation,
      );
      if (!preserveTransition) {
        topologyTransitionRef.current = false;
        transitionSnapshotSocketRef.current = null;
        topologyTransitionCleanupRef.current = Promise.resolve();
        topologyTransitionCompletionRef.current = Promise.resolve();
      }
      warmSocketRebindGenerationRef.current = null;
      ownershipGenerationRef.current += 1;
      const preparedPlayback = preparedPlaybackRef.current;
      preparedPlaybackRef.current = null;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      const playback = playbackRef.current;
      playbackRef.current = null;
      const openAITransport = openAITransportRef.current;
      openAITransportRef.current = null;
      openAITransportPausedRef.current = false;
      const socket = socketRef.current;
      socketRef.current = null;
      pttTurnRef.current = null;
      if (publish && !disposedRef.current) {
        clearInputAnalysis();
        clearOutputAnalysis();
        setMediaWarning(null);
      }
      const attempts: Array<() => unknown> = [];
      attempts.push(() => openingAllocationRelease);
      if (recorder) attempts.push(() => recorder.stop());
      if (playback) {
        attempts.push(() => playback.interrupt());
        attempts.push(() => playback.close());
      }
      if (openAITransport) attempts.push(() => openAITransport.close());
      if (
        preparedPlayback?.prearmedContext &&
        preparedPlayback.prearmedContext.state !== 'closed'
      ) {
        attempts.push(() => preparedPlayback.prearmedContext!.close());
      }
      if (socket) attempts.push(() => socket.end());
      const releases = attempts.map((attempt) => {
        try {
          return Promise.resolve(attempt());
        } catch (cleanupError: unknown) {
          return Promise.reject(cleanupError);
        }
      });
      const results = await Promise.allSettled(releases);
      const failure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected',
      );
      if (failure) throw failure.reason;
    },
    [disposeOpeningAllocation],
  );

  const gateOpenAIRealtimeMedia = useCallback(
    (publish = true): Promise<void> => {
      const openAITransport = openAITransportRef.current;
      if (!openAITransport || openAITransportPausedRef.current) {
        return Promise.resolve();
      }
      openAITransportPausedRef.current = true;
      if (publish && !disposedRef.current) {
        clearInputAnalysis();
        clearOutputAnalysis();
      }
      try {
        return Promise.resolve(openAITransport.pause()).then(() => {
          if (
            publish &&
            !disposedRef.current &&
            openAITransportRef.current === openAITransport &&
            openAITransportPausedRef.current
          ) {
            setMediaWarning(null);
          }
        });
      } catch (pauseError: unknown) {
        return Promise.reject(pauseError);
      }
    },
    [],
  );

  const suspendMedia = useCallback(
    async (publish = true): Promise<void> => {
      mediaReadyRef.current = false;
      mediaAttachedRef.current = false;
      socketReadyRef.current = false;
      const openAITransport = openAITransportRef.current;
      if (!openAITransport) {
        await releaseMedia(publish);
        return;
      }
      const gated = gateOpenAIRealtimeMedia(publish);
      const socket = socketRef.current;
      socketRef.current = null;
      if (publish && !disposedRef.current) {
        clearInputAnalysis();
        clearOutputAnalysis();
      }
      const attempts: Array<() => unknown> = [];
      attempts.push(() => gated);
      if (socket) attempts.push(() => socket.end());
      const results = await Promise.allSettled(
        attempts.map((attempt) => {
          try {
            return Promise.resolve(attempt());
          } catch (cleanupError: unknown) {
            return Promise.reject(cleanupError);
          }
        }),
      );
      const failure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected',
      );
      if (failure) throw failure.reason;
    },
    [gateOpenAIRealtimeMedia, releaseMedia],
  );

  const applySession = useCallback(
    (
      next: VoiceSessionSnapshot,
      source: SessionSource = 'remote',
      snapshotSource?: VoiceSocketLike,
    ): void => {
      const current = sessionRef.current;
      if (
        current?.sessionId === next.sessionId &&
        next.leaseVersion < current.leaseVersion
      )
        return;
      if (current && rejectsMediaAuthorityTransition(current, next)) return;
      const hasMediaOwnership = Boolean(
        recorderRef.current ||
        playbackRef.current ||
        openAITransportRef.current ||
        socketRef.current,
      );
      const topologyChanged = Boolean(
        source === 'remote' &&
        (hasMediaOwnership || topologyTransitionRef.current) &&
        (current?.state === 'active' || current?.state === 'reconnecting') &&
        next.state === 'active' &&
        current.sessionId === next.sessionId &&
        !sameMediaTopology(current, next),
      );
      // Reconnecting keeps its socket/media so VoiceSocket can recover in place.
      // Local pause keeps its awaited cleanup in pauseCurrent; remote pause and
      // every ended authority must detach immediately.
      const releasesMedia =
        next.state === 'ended' ||
        (source === 'remote' && next.state === 'paused');
      if (
        pttTurnRef.current &&
        !releasesMedia &&
        !topologyChanged &&
        (next.state !== 'active' ||
          current?.sessionId !== next.sessionId ||
          current.currentAgentId !== next.currentAgentId)
      ) {
        const cleanupAuthority = {
          lifecycleGeneration: lifecycleGenerationRef.current,
          ownershipGeneration: ownershipGenerationRef.current,
          sessionId: next.sessionId,
          leaseVersion: next.leaseVersion,
          agentId: next.currentAgentId,
        };
        void cancelPtt().catch((cancelError: unknown) => {
          const current = sessionRef.current;
          if (
            isLifecycleCurrent(cleanupAuthority.lifecycleGeneration) &&
            ownershipGenerationRef.current ===
              cleanupAuthority.ownershipGeneration &&
            current?.sessionId === cleanupAuthority.sessionId &&
            current.leaseVersion === cleanupAuthority.leaseVersion &&
            current.currentAgentId === cleanupAuthority.agentId
          ) {
            setError(
              cancelError instanceof Error
                ? cancelError.message
                : 'Could not release voice input',
            );
          }
        });
      }
      if (next.state === 'ended') activeOpenAuthorityRef.current = null;
      const activeOpen = activeOpenAuthorityRef.current;
      const claimKey = claimSession(
        next,
        lifecycleGenerationRef.current,
        activeOpen?.openRequest ?? latestOpenRequestRef.current,
      );
      flushDeferredStaleSessions(claimKey);
      sessionRef.current = next;
      setSession(next);
      if (topologyChanged) setError(null);
      if (
        !topologyChanged &&
        next.state === 'active' &&
        mediaAttachedRef.current &&
        socketReadyRef.current
      ) {
        mediaReadyRef.current = true;
      }
      setStatus(
        topologyChanged
          ? 'reconnecting'
          : next.state === 'active'
            ? mediaReadyRef.current
              ? 'active'
              : 'opening'
            : next.state === 'paused'
              ? 'paused'
              : next.state === 'ended'
                ? 'ended'
                : 'reconnecting',
      );
      if (next.state === 'paused') setModelState('paused');
      if (next.state === 'active' || next.state === 'ended')
        setModelState(null);
      if (releasesMedia) {
        mediaReadyRef.current = false;
        const cleanupAuthority = {
          lifecycleGeneration: lifecycleGenerationRef.current,
          sessionId: next.sessionId,
          leaseVersion: next.leaseVersion,
          agentId: next.currentAgentId,
        };
        const mediaRelease =
          next.state === 'paused' && openAITransportRef.current
            ? suspendMedia()
            : releaseMedia();
        void mediaRelease.catch((cleanupError: unknown) => {
          const authoritative = sessionRef.current;
          if (
            isLifecycleCurrent(cleanupAuthority.lifecycleGeneration) &&
            authoritative?.sessionId === cleanupAuthority.sessionId &&
            authoritative.leaseVersion === cleanupAuthority.leaseVersion &&
            authoritative.currentAgentId === cleanupAuthority.agentId &&
            authoritative.state === next.state
          ) {
            setError(
              cleanupError instanceof Error
                ? cleanupError.message
                : 'Could not release voice media',
            );
          }
        });
      } else if (topologyChanged) {
        const snapshotSocket =
          snapshotSource ??
          transitionSnapshotSocketRef.current ??
          socketRef.current;
        topologyTransitionRef.current = true;
        if (snapshotSocket)
          transitionSnapshotSocketRef.current = snapshotSocket;
        const transitionAuthority = {
          lifecycleGeneration: lifecycleGenerationRef.current,
          sessionId: next.sessionId,
          topology: mediaTopologyIdentity(next),
        };
        const previousCleanup = topologyTransitionCleanupRef.current;
        const currentCleanup = releaseMedia(true, true);
        const transitionCleanup = Promise.all([
          previousCleanup,
          currentCleanup,
        ]).then(() => undefined);
        topologyTransitionCleanupRef.current = transitionCleanup;
        const transitionCompletion = transitionCleanup.then(async () => {
          const authoritative = sessionRef.current;
          if (
            isLifecycleCurrent(transitionAuthority.lifecycleGeneration) &&
            authoritative?.sessionId === transitionAuthority.sessionId &&
            isCurrentMediaTopology(authoritative, next) &&
            mediaTopologyIdentity(authoritative) ===
              transitionAuthority.topology
          ) {
            await reconfigureMediaRef.current(
              authoritative,
              transitionAuthority.lifecycleGeneration,
            );
          }
        });
        topologyTransitionCompletionRef.current = transitionCompletion;
        void transitionCompletion.catch((cleanupError: unknown) => {
          const authoritative = sessionRef.current;
          if (
            isLifecycleCurrent(transitionAuthority.lifecycleGeneration) &&
            authoritative?.sessionId === transitionAuthority.sessionId &&
            isCurrentMediaTopology(authoritative, next) &&
            mediaTopologyIdentity(authoritative) ===
              transitionAuthority.topology
          ) {
            setStatus('error');
            topologyTransitionRef.current = false;
            transitionSnapshotSocketRef.current = null;
            topologyTransitionCleanupRef.current = Promise.resolve();
            setError(
              cleanupError instanceof Error
                ? cleanupError.message
                : 'Could not reconfigure voice media',
            );
          }
        });
      }
    },
    [
      cancelPtt,
      claimSession,
      flushDeferredStaleSessions,
      isLifecycleCurrent,
      releaseMedia,
      suspendMedia,
    ],
  );

  const handleEvent = useCallback((event: NormalizedVoiceEvent): void => {
    if (event.type === 'usage_reported') {
      // The server contract must publish this only after durable settlement.
      // It is an observation signal, not a client-side cost calculation.
      setProviderUsageObservationGeneration((generation) => generation + 1);
      return;
    }
    if (event.type === 'state_changed') {
      setModelState(event.state);
      if (event.state === 'speaking') {
        openAITransportRef.current?.releaseOutput?.();
      }
      return;
    }
    if (event.type === 'audio_output') {
      playbackRef.current?.enqueue(
        decodeBase64(event.chunk.dataBase64),
        event.chunk.sampleRateHz,
      );
      return;
    }
    if (event.type === 'interrupted') {
      playbackRef.current?.interrupt();
      openAITransportRef.current?.interrupt();
      clearOutputAnalysis();
      if (event.reason === 'user_barge_in') setBargeInAt(Date.now());
      return;
    }
    if (event.type === 'turn_completed') {
      playbackRef.current?.complete();
      return;
    }
    if (
      event.type === 'input_transcript_delta' ||
      event.type === 'input_transcript_final' ||
      event.type === 'output_transcript_delta' ||
      event.type === 'output_transcript_final'
    ) {
      const side = event.type.startsWith('input_') ? 'input' : 'output';
      const value = 'delta' in event ? event.delta : event.text;
      const agentId = sessionRef.current?.currentAgentId;
      const agentName = sessionRef.current?.currentAgentName;
      setProvisionalTranscripts((current) => {
        const turn = current[event.turnKey] ?? {
          input: '',
          output: '',
          agentId,
          agentName,
        };
        return {
          ...current,
          [event.turnKey]: {
            ...turn,
            [side]: 'delta' in event ? `${turn[side]}${value}` : value,
          },
        };
      });
      return;
    }
    if (event.type === 'error') setError(event.message);
  }, []);

  const reconcileDeferredStart = useCallback(
    (
      currentSession: VoiceSessionSnapshot,
      currentAuthority: VoiceOpenAuthority,
    ): VoiceSessionSnapshot => {
      if (
        !hasActiveLease(currentSession) ||
        !sessionMatchesStartAuthority(currentSession, currentAuthority)
      ) {
        endUnclaimedSession(currentSession);
        throw new Error('Voice session renewal authority changed; retry');
      }
      let authoritative = currentSession;
      const sameSessionCandidates = [
        ...deferredStaleSessionsRef.current.entries(),
      ].filter(
        ([, candidate]) =>
          candidate.session.sessionId === currentSession.sessionId,
      );
      for (const [, candidate] of sameSessionCandidates) {
        if (
          compatibleStartRenewal(candidate, currentAuthority, currentSession)
        ) {
          if (candidate.session.leaseVersion > authoritative.leaseVersion) {
            authoritative = candidate.session;
          }
        }
      }
      const conflict = sameSessionCandidates.some(([, candidate]) => {
        if (candidate.session.leaseVersion < authoritative.leaseVersion) {
          return false;
        }
        if (candidate.session.leaseVersion > authoritative.leaseVersion) {
          return true;
        }
        if (!sameSnapshotAuthority(candidate.session, authoritative)) {
          return true;
        }
        return (
          candidate.origin.kind === 'start' &&
          !compatibleStartRenewal(candidate, currentAuthority, currentSession)
        );
      });
      if (conflict) {
        endUnclaimedSession(currentSession);
        throw new Error('Voice session renewal authority changed; retry');
      }
      for (const [candidateKey, candidate] of sameSessionCandidates) {
        if (
          compatibleStartRenewal(candidate, currentAuthority, currentSession)
        ) {
          deferredStaleSessionsRef.current.delete(candidateKey);
        }
      }
      return authoritative;
    },
    [endUnclaimedSession],
  );

  const disposeStaleSession = useCallback(
    (
      staleSession: VoiceSessionSnapshot,
      staleLifecycleGeneration: number,
      origin: StaleResponseOrigin,
    ): void => {
      if (staleSession.state === 'ended' || !staleSession.leaseActive) return;
      const key = voiceSessionKey(staleSession);
      const claim = sessionClaimRef.current;
      const activeOpen = activeOpenAuthorityRef.current;
      const activeClaimed =
        activeOpen != null &&
        claim?.lifecycleGeneration === activeOpen.lifecycleGeneration &&
        claim.openRequest === activeOpen.openRequest;
      const current = sessionRef.current;
      const candidate = { session: staleSession, origin };

      const terminateConflictingClaim = (): void => {
        lifecycleGenerationRef.current += 1;
        latestOpenRequestRef.current += 1;
        activeOpenAuthorityRef.current = null;
        sessionClaimRef.current = null;
        const deferred = [...deferredStaleSessionsRef.current.values()];
        deferredStaleSessionsRef.current.clear();
        sessionRef.current = null;
        setSession(null);
        setModelState(null);
        setProvisionalTranscripts({});
        setStatus('error');
        setError('Voice session renewal authority changed; retry');
        if (current) endUnclaimedSession(current);
        endUnclaimedSession(staleSession);
        for (const deferredCandidate of deferred) {
          endUnclaimedSession(deferredCandidate.session);
        }
        void releaseMedia().catch(() => undefined);
      };

      if (
        activeOpen != null &&
        activeClaimed &&
        current?.sessionId === staleSession.sessionId
      ) {
        const leaseDifference =
          staleSession.leaseVersion - current.leaseVersion;
        const compatibleStart = compatibleStartRenewal(
          candidate,
          activeOpen,
          current,
        );
        if (leaseDifference === 0) {
          if (
            sameSnapshotAuthority(staleSession, current) &&
            (origin.kind === 'command' || compatibleStart)
          ) {
            return;
          }
          terminateConflictingClaim();
          return;
        }
        if (leaseDifference > 0) {
          if (origin.kind === 'start' && compatibleStart) {
            applySession(staleSession);
            return;
          }
          terminateConflictingClaim();
          return;
        }
        if (origin.kind === 'start' && compatibleStart) {
          return;
        }
      }

      if (claim?.key === key) return;

      if (
        !disposedRef.current &&
        activeOpen != null &&
        activeOpen.lifecycleGeneration > staleLifecycleGeneration &&
        !activeClaimed
      ) {
        const candidateKey = deferredCandidateKey(candidate);
        const existing = deferredStaleSessionsRef.current.get(candidateKey);
        if (existing) {
          if (staleSession.leaseVersion > existing.session.leaseVersion) {
            deferredStaleSessionsRef.current.set(candidateKey, candidate);
          }
          return;
        }
        if (
          deferredStaleSessionsRef.current.size < MAX_DEFERRED_STALE_SESSIONS
        ) {
          deferredStaleSessionsRef.current.set(candidateKey, candidate);
          return;
        }

        latestOpenRequestRef.current += 1;
        activeOpenAuthorityRef.current = null;
        sessionClaimRef.current = null;
        const deferred = [
          ...[...deferredStaleSessionsRef.current.values()].map(
            (entry) => entry.session,
          ),
          staleSession,
        ];
        deferredStaleSessionsRef.current.clear();
        if (!disposedRef.current) {
          setStatus('error');
          setError('Voice cleanup capacity was exceeded; retry');
        }
        for (const deferredSession of deferred) {
          endUnclaimedSession(deferredSession);
        }
        return;
      }
      endUnclaimedSession(staleSession);
    },
    [applySession, endUnclaimedSession, releaseMedia],
  );

  const createRecorder = useCallback(
    (socket: VoiceSocketLike, generation: number): RecorderLike => {
      const current = sessionRef.current;
      let recorder: RecorderLike;
      recorder = dependencies.createRecorder({
        sampleRateHz: INPUT_SAMPLE_RATE_HZ,
        onChunk: (dataBase64) => {
          if (
            disposedRef.current ||
            mutedRef.current ||
            ownershipGenerationRef.current !== generation ||
            socketRef.current !== socket ||
            recorderRef.current !== recorder
          )
            return;
          const profile = sessionRef.current?.profileSnapshot.profile;
          socket.sendAudio({
            dataBase64,
            mimeType: profile?.input.mimeType ?? 'audio/pcm',
            sampleRateHz: INPUT_SAMPLE_RATE_HZ,
          });
        },
        onLevel: (level) => {
          if (
            !disposedRef.current &&
            mediaReadyRef.current &&
            ownershipGenerationRef.current === generation &&
            socketRef.current === socket &&
            recorderRef.current === recorder
          ) {
            setInputLevel(level);
          }
        },
        onSpectrum: (spectrum) => {
          if (
            !disposedRef.current &&
            mediaReadyRef.current &&
            ownershipGenerationRef.current === generation &&
            socketRef.current === socket &&
            recorderRef.current === recorder
          ) {
            setInputSpectrum([...spectrum]);
          }
        },
      });
      recorderRef.current = recorder;
      if (!current) throw new Error('Voice session is unavailable');
      return recorder;
    },
    [dependencies],
  );

  const createPlayback = useCallback(
    (generation: number, outputElement?: HTMLAudioElement): PlaybackLike => {
      let playback: PlaybackLike;
      playback = dependencies.createPlayback({
        ...(outputElement ? { outputElement } : {}),
        onLevel: (level) => {
          if (
            !disposedRef.current &&
            mediaReadyRef.current &&
            ownershipGenerationRef.current === generation &&
            playbackRef.current === playback
          ) {
            setOutputLevel(level);
          }
        },
        onSpectrum: (spectrum) => {
          if (
            !disposedRef.current &&
            mediaReadyRef.current &&
            ownershipGenerationRef.current === generation &&
            playbackRef.current === playback
          ) {
            setOutputSpectrum([...spectrum]);
          }
        },
      });
      playbackRef.current = playback;
      return playback;
    },
    [dependencies],
  );

  const preparePlayback = useCallback(
    (
      outputDeviceId: string | undefined,
      outputElement: HTMLAudioElement,
      inputDeviceId?: string,
    ): PreparedPlayback => {
      const generation = ++ownershipGenerationRef.current;
      const playback = createPlayback(generation, outputElement);
      const prepared: PreparedPlayback = {
        playback,
        generation,
        ready: Promise.resolve(),
        outputElement,
        ...(inputDeviceId ? { inputDeviceId } : {}),
        ...(outputDeviceId ? { outputDeviceId } : {}),
      };
      preparedPlaybackRef.current = prepared;
      let firstResume: Promise<void>;
      try {
        firstResume = Promise.resolve(playback.resume(outputDeviceId));
      } catch (resumeError: unknown) {
        firstResume = Promise.reject(resumeError);
      }
      const ready = firstResume.catch(async (deviceError: unknown) => {
        if (
          disposedRef.current ||
          ownershipGenerationRef.current !== generation ||
          playbackRef.current !== playback
        ) {
          throw new Error('Voice channel ownership was disposed');
        }
        if (!outputDeviceId) throw deviceError;
        const devices = readDevices(dependencies.storage);
        writeDevices(dependencies.storage, { ...devices, output: undefined });
        await playback.resume();
      });
      const createAudioContext =
        dependencies.createAudioContext ??
        (typeof AudioContext === 'function'
          ? () => new AudioContext()
          : undefined);
      let contextReady: Promise<void> = Promise.resolve();
      try {
        const prearmedContext = createAudioContext?.();
        if (prearmedContext) {
          prepared.prearmedContext = prearmedContext;
          if (prearmedContext.state === 'suspended') {
            contextReady = Promise.resolve(prearmedContext.resume());
          }
        }
      } catch (contextError: unknown) {
        contextReady = Promise.reject(contextError);
      }
      prepared.ready = Promise.all([ready, contextReady]).then(() => undefined);
      return prepared;
    },
    [createPlayback, dependencies.storage],
  );

  const publishMediaReady = useCallback(
    (next: VoiceSessionSnapshot, generation: number): void => {
      if (
        disposedRef.current ||
        ownershipGenerationRef.current !== generation ||
        !mediaAttachedRef.current ||
        !socketReadyRef.current ||
        !isCurrentMediaTopology(sessionRef.current, next) ||
        sessionRef.current?.state !== 'active'
      ) {
        return;
      }
      mediaReadyRef.current = true;
      setStatus('active');
    },
    [],
  );

  const attachSocket = useCallback(
    (next: VoiceSessionSnapshot, generation: number): void => {
      let socket: VoiceSocketLike;
      const isSocketOwner = (): boolean =>
        !disposedRef.current &&
        ownershipGenerationRef.current === generation &&
        socketRef.current === socket;
      const isSnapshotAuthority = (): boolean =>
        isSocketOwner() ||
        (!disposedRef.current &&
          topologyTransitionRef.current &&
          transitionSnapshotSocketRef.current === socket);
      socket = dependencies.createSocket({
        path: next.transport.websocketPath,
        expectedSessionId: next.sessionId,
        expectedLeaseVersion: next.leaseVersion,
        onSnapshot: (snapshot) => {
          if (isSnapshotAuthority()) {
            if (warmSocketRebindGenerationRef.current === generation) {
              warmSocketRebindGenerationRef.current = null;
            }
            applySession(snapshot, 'remote', socket);
          }
        },
        onEvent: (event) => {
          if (isSocketOwner()) handleEvent(event);
        },
        onError: (socketError) => {
          if (isSocketOwner()) setError(socketError.message);
        },
        onConnectionChange: (connectionState) => {
          if (!isSocketOwner()) return;
          if (connectionState === 'connected') {
            socketReadyRef.current = true;
            publishMediaReady(next, generation);
            return;
          }
          socketReadyRef.current = false;
          mediaReadyRef.current = false;
          clearInputAnalysis();
          clearOutputAnalysis();
          if (connectionState === 'connecting') {
            setStatus(
              sessionRef.current?.state === 'reconnecting'
                ? 'reconnecting'
                : 'opening',
            );
          } else if (connectionState === 'reconnecting') {
            setStatus('reconnecting');
          }
        },
        onRecoveryRequired: (reason) => {
          if (!isSocketOwner() || coldRecoverySocketsRef.current.has(socket)) {
            return;
          }
          if (
            reason.code === 'voice_socket_reconnect_exhausted' &&
            warmSocketRebindGenerationRef.current !== generation
          ) {
            warmSocketRebindGenerationRef.current = generation;
            const current = sessionRef.current;
            if (current && isCurrentMediaTopology(current, next)) {
              attachSocket(current, generation);
              return;
            }
          }
          coldRecoverySocketsRef.current.add(socket);
          setStatus('reconnecting');
          void serialize(() =>
            recoverColdRuntimeRef.current(socket, generation),
          ).catch((recoveryError: unknown) => {
            if (
              !disposedRef.current &&
              sessionRef.current?.state !== 'paused' &&
              sessionRef.current?.state !== 'ended'
            ) {
              setStatus('error');
              setError(
                recoveryError instanceof Error
                  ? recoveryError.message
                  : 'Voice session recovery failed',
              );
            }
          });
        },
      });
      socketReadyRef.current = false;
      socketRef.current = socket;
      socket.connect();
    },
    [
      applySession,
      clearInputAnalysis,
      clearOutputAnalysis,
      dependencies,
      handleEvent,
      publishMediaReady,
      serialize,
    ],
  );

  const attachOpenAIRealtimeMedia = useCallback(
    async (
      next: VoiceSessionSnapshot,
      generation: number,
      preparedPlayback?: PreparedPlayback,
      preparedInputStream?: MediaStream,
    ): Promise<void> => {
      const authority = requireMediaAuthority(next);
      const assertOwned = (): void => {
        if (
          disposedRef.current ||
          ownershipGenerationRef.current !== generation ||
          !sameSessionMediaBindingGeneration(
            mediaAuthority(sessionRef.current),
            authority,
          )
        ) {
          throw new Error('Voice channel ownership was disposed');
        }
      };
      const outputElement =
        preparedPlayback?.outputElement ??
        dependencies.createAudioElement?.() ??
        new Audio();
      if (preparedPlayback) {
        await preparedPlayback.playback.close();
        if (playbackRef.current === preparedPlayback.playback) {
          playbackRef.current = null;
        }
      }
      assertOwned();
      const devices = readDevices(dependencies.storage);
      const inputDeviceId = preparedPlayback?.inputDeviceId ?? devices.input;
      const outputDeviceId = preparedPlayback?.outputDeviceId ?? devices.output;
      const getUserMedia =
        dependencies.getUserMedia ??
        ((constraints: MediaStreamConstraints) =>
          navigator.mediaDevices.getUserMedia(constraints));
      let stream = preparedInputStream;
      if (!stream) {
        try {
          stream = await getUserMedia({
            audio: realtimeMicrophoneConstraints(inputDeviceId),
            video: false,
          });
        } catch (deviceError: unknown) {
          const invalidSavedDevice =
            inputDeviceId &&
            deviceError instanceof DOMException &&
            ['NotFoundError', 'OverconstrainedError'].includes(
              deviceError.name,
            );
          if (!invalidSavedDevice) throw deviceError;
          assertOwned();
          writeDevices(dependencies.storage, { ...devices, input: undefined });
          stream = await getUserMedia({
            audio: realtimeMicrophoneConstraints(),
            video: false,
          });
        }
      }
      try {
        assertOwned();
      } catch (ownershipError: unknown) {
        for (const track of stream.getTracks()) track.stop();
        if (
          preparedPlayback?.prearmedContext &&
          preparedPlayback.prearmedContext.state !== 'closed'
        ) {
          await preparedPlayback.prearmedContext.close();
        }
        throw ownershipError;
      }
      setMediaWarning(hardenRealtimeMicrophoneStream(stream));
      let transport: OpenAIRealtimeTransportLike;
      const isTransportOwner = (): boolean =>
        !disposedRef.current &&
        ownershipGenerationRef.current === generation &&
        openAITransportRef.current === transport;
      const createTransport =
        dependencies.createOpenAITransport ??
        ((options: OpenAIRealtimeTransportOptions) =>
          new OpenAIRealtimeTransport(options));
      transport = createTransport({
        onConnectionState: (connectionState) => {
          if (!isTransportOwner()) return;
          if (connectionState === 'reconnecting') {
            mediaReadyRef.current = false;
            clearInputAnalysis();
            clearOutputAnalysis();
            setStatus('reconnecting');
          } else if (connectionState === 'failed') {
            mediaReadyRef.current = false;
            clearInputAnalysis();
            clearOutputAnalysis();
            setStatus('error');
          } else if (
            connectionState === 'connected' &&
            sessionRef.current?.state === 'active'
          ) {
            publishMediaReady(next, generation);
          }
        },
        onInputLevel: (level) => {
          if (isTransportOwner() && mediaReadyRef.current) setInputLevel(level);
        },
        onOutputLevel: (level) => {
          if (isTransportOwner() && mediaReadyRef.current)
            setOutputLevel(level);
        },
        onInputSpectrum: (spectrum) => {
          if (isTransportOwner() && mediaReadyRef.current)
            setInputSpectrum([...spectrum]);
        },
        onOutputSpectrum: (spectrum) => {
          if (isTransportOwner() && mediaReadyRef.current)
            setOutputSpectrum([...spectrum]);
        },
        onError: (transportError) => {
          if (!isTransportOwner()) return;
          setError(transportError.message);
          if (!transportError.recoverable) setStatus('error');
        },
      });
      openAITransportRef.current = transport;
      openAITransportPausedRef.current = false;
      transport.setMuted?.(mutedRef.current);
      attachSocket(next, generation);
      try {
        const connecting = transport.connect({
          authority,
          stream,
          outputElement,
          ...(preparedPlayback?.prearmedContext
            ? { prearmedContext: preparedPlayback.prearmedContext }
            : {}),
          ...(outputDeviceId ? { outputDeviceId } : {}),
          negotiateOffer: async (offerAuthority, offerSdp) => {
            if (!sameSessionMediaBindingGeneration(offerAuthority, authority)) {
              throw new Error('Voice media authority changed during offer');
            }
            const requestAuthority = mediaAuthority(sessionRef.current);
            if (
              !requestAuthority ||
              !sameSessionMediaBindingGeneration(requestAuthority, authority)
            ) {
              throw new Error('Voice media authority changed during offer');
            }
            let response;
            try {
              response = await defaultApi.createOpenAIRealtimeOffer(
                requestAuthority.sessionId,
                requestAuthority.leaseVersion,
                requestAuthority.mediaBindingId,
                requestAuthority.mediaGeneration,
                offerSdp,
              );
            } catch (offerError: unknown) {
              throw negotiationTransportError(offerError);
            }
            if (
              !sameMediaAuthority(
                mediaAuthority(sessionRef.current),
                requestAuthority,
              )
            ) {
              throw new Error('Voice media authority changed during offer');
            }
            return response.data;
          },
        });
        if (preparedPlaybackRef.current === preparedPlayback) {
          preparedPlaybackRef.current = null;
        }
        await connecting;
      } catch (connectError: unknown) {
        if (openAITransportRef.current !== transport) {
          await transport.close();
        }
        throw connectError;
      }
      assertOwned();
    },
    [attachSocket, defaultApi, dependencies, publishMediaReady],
  );

  const attachMedia = useCallback(
    async (
      next: VoiceSessionSnapshot,
      generation: number,
      preparedPlayback?: PreparedPlayback,
      preparedInputStream?: MediaStream,
    ): Promise<void> => {
      const assertOwned = (): void => {
        if (
          disposedRef.current ||
          ownershipGenerationRef.current !== generation
        ) {
          throw new Error('Voice channel ownership was disposed');
        }
      };
      assertOwned();
      if (next.transport.type === 'webrtc') {
        await attachOpenAIRealtimeMedia(
          next,
          generation,
          preparedPlayback,
          preparedInputStream,
        );
        return;
      }
      if (
        preparedPlayback?.prearmedContext &&
        preparedPlayback.prearmedContext.state !== 'closed'
      ) {
        await preparedPlayback.prearmedContext.close();
      }
      if (preparedPlaybackRef.current === preparedPlayback) {
        preparedPlaybackRef.current = null;
      }
      const playback = preparedPlayback?.playback ?? createPlayback(generation);
      if (!preparedPlayback) {
        const outputDeviceId = readDevices(dependencies.storage).output;
        try {
          await playback.resume(outputDeviceId);
        } catch (deviceError: unknown) {
          if (!outputDeviceId) throw deviceError;
          const devices = readDevices(dependencies.storage);
          writeDevices(dependencies.storage, { ...devices, output: undefined });
          await playback.resume();
        }
      }
      assertOwned();
      attachSocket(next, generation);
      if (next.profileSnapshot.profile.mode === 'realtime') {
        const socket = socketRef.current;
        if (!socket) throw new Error('Voice socket is unavailable');
        const recorder = createRecorder(socket, generation);
        await recorder.start(readDevices(dependencies.storage).input);
        assertOwned();
      }
    },
    [
      attachOpenAIRealtimeMedia,
      attachSocket,
      createPlayback,
      createRecorder,
      dependencies.storage,
    ],
  );

  const attachMediaSafely = useCallback(
    async (
      next: VoiceSessionSnapshot,
      lifecycleGeneration = lifecycleGenerationRef.current,
      preparedPlayback?: PreparedPlayback,
      preparedInputStream?: MediaStream,
    ): Promise<void> => {
      if (!isLifecycleCurrent(lifecycleGeneration)) {
        throw new Error('Voice channel ownership was disposed');
      }
      const generation =
        preparedPlayback?.generation ?? ++ownershipGenerationRef.current;
      mediaReadyRef.current = false;
      mediaAttachedRef.current = false;
      clearInputAnalysis();
      clearOutputAnalysis();
      try {
        if (preparedPlayback) await preparedPlayback.ready;
        await attachMedia(
          next,
          generation,
          preparedPlayback,
          preparedInputStream,
        );
        if (!isLifecycleCurrent(lifecycleGeneration)) {
          throw new Error('Voice channel ownership was disposed');
        }
        mediaAttachedRef.current = true;
        publishMediaReady(next, generation);
      } catch (attachError: unknown) {
        let cleanupError: unknown;
        if (ownershipGenerationRef.current === generation) {
          try {
            await releaseMedia();
          } catch (releaseError: unknown) {
            cleanupError = releaseError;
          }
        }
        const publishedError = cleanupError ?? attachError;
        const controlledOpeningCancellation = sameMediaAuthority(
          mediaAuthority(next),
          gatedOpeningAuthorityRef.current,
        );
        if (
          !controlledOpeningCancellation &&
          isLifecycleCurrent(lifecycleGeneration) &&
          isCurrentMediaTopology(sessionRef.current, next)
        ) {
          setStatus('error');
          setError(
            publishedError instanceof Error
              ? publishedError.message
              : 'Could not open voice',
          );
        }
        throw publishedError;
      }
    },
    [
      attachMedia,
      clearInputAnalysis,
      clearOutputAnalysis,
      isLifecycleCurrent,
      publishMediaReady,
      releaseMedia,
    ],
  );

  reconfigureMediaRef.current = (
    next: VoiceSessionSnapshot,
    lifecycleGeneration: number,
  ): Promise<void> =>
    attachMediaSafely(next, lifecycleGeneration).then(
      () => {
        if (
          isLifecycleCurrent(lifecycleGeneration) &&
          isCurrentMediaTopology(sessionRef.current, next)
        ) {
          topologyTransitionRef.current = false;
          transitionSnapshotSocketRef.current = null;
          topologyTransitionCleanupRef.current = Promise.resolve();
          if (sessionRef.current?.state === 'active') setStatus('active');
        }
      },
      (attachError: unknown) => {
        if (
          isLifecycleCurrent(lifecycleGeneration) &&
          isCurrentMediaTopology(sessionRef.current, next)
        ) {
          topologyTransitionRef.current = false;
          transitionSnapshotSocketRef.current = null;
          topologyTransitionCleanupRef.current = Promise.resolve();
        }
        throw attachError;
      },
    );

  const command = useCallback(
    async (
      current: VoiceSessionSnapshot,
      body: VoiceSessionCommandRequest,
      lifecycleGeneration = lifecycleGenerationRef.current,
      publish = true,
    ): Promise<VoiceSessionSnapshot> => {
      const next = await remoteAuthorityRegistry.execute(current, body);
      if (isLifecycleCurrent(lifecycleGeneration)) {
        if (publish) applySession(next, 'local');
      } else {
        disposeStaleSession(next, lifecycleGeneration, {
          kind: 'command',
        });
      }
      return next;
    },
    [
      applySession,
      disposeStaleSession,
      isLifecycleCurrent,
      remoteAuthorityRegistry,
    ],
  );

  const pauseCurrent = useCallback(
    async (
      publish = true,
      lifecycleGeneration = lifecycleGenerationRef.current,
    ): Promise<void> => {
      if (!isLifecycleCurrent(lifecycleGeneration)) return;
      const current = sessionRef.current;
      if (!current) return;
      const ownershipGeneration = ownershipGenerationRef.current;
      let commandFailed = false;
      try {
        if (current.state !== 'paused' && current.state !== 'ended') {
          await command(
            current,
            {
              commandId: dependencies.randomUUID(),
              leaseVersion: current.leaseVersion,
              type: 'pause',
            },
            lifecycleGeneration,
            publish,
          );
        }
      } catch (pauseError: unknown) {
        commandFailed = true;
        throw pauseError;
      } finally {
        const authoritative = sessionRef.current;
        if (
          isLifecycleCurrent(lifecycleGeneration) &&
          authoritative?.sessionId === current.sessionId &&
          (authoritative.state === 'paused' ||
            (commandFailed &&
              ownershipGenerationRef.current === ownershipGeneration))
        ) {
          if (openAITransportRef.current) await suspendMedia();
          else await releaseMedia();
        }
      }
    },
    [command, dependencies, isLifecycleCurrent, releaseMedia, suspendMedia],
  );

  const openForConversation = useCallback(
    async (
      conversationId: number,
      input: OpenVoiceChannelInput,
      lifecycleGeneration: number,
      openRequest: number,
      predecessorRelease: Promise<void>,
      preparedPlayback: PreparedPlayback,
    ): Promise<void> => {
      let attachingSession: VoiceSessionSnapshot | null = null;
      const assertCurrentRequest = (): void => {
        if (
          !isLifecycleCurrent(lifecycleGeneration) ||
          latestOpenRequestRef.current !== openRequest
        ) {
          throw new Error('Voice channel ownership was disposed');
        }
      };
      try {
        await Promise.all([predecessorRelease, preparedPlayback.ready]);
        assertCurrentRequest();
        if (latestOpenRequestRef.current === openRequest) {
          setSession(null);
          setStatus('opening');
          setModelState(null);
          setProvisionalTranscripts({});
          setError(null);
        }
        const response = await defaultApi.startVoiceSession(
          input.teamId,
          conversationId,
          { commandId: dependencies.randomUUID(), agentId: input.agentId },
        );
        const startOrigin: StaleResponseOrigin = {
          kind: 'start',
          authority: {
            teamId: input.teamId,
            conversationId,
            agentId: input.agentId,
          },
        };
        if (disposedRef.current) {
          disposeStaleSession(response.data, lifecycleGeneration, startOrigin);
          return;
        }
        if (
          !isLifecycleCurrent(lifecycleGeneration) ||
          latestOpenRequestRef.current !== openRequest
        ) {
          disposeStaleSession(response.data, lifecycleGeneration, startOrigin);
          throw new Error('Voice channel ownership was disposed');
        }
        const activeOpen = activeOpenAuthorityRef.current;
        if (
          activeOpen?.lifecycleGeneration !== lifecycleGeneration ||
          activeOpen.openRequest !== openRequest
        ) {
          disposeStaleSession(response.data, lifecycleGeneration, startOrigin);
          throw new Error('Voice channel ownership was disposed');
        }
        const authoritative = reconcileDeferredStart(response.data, activeOpen);
        attachingSession = authoritative;
        conversationRef.current = conversationId;
        setProvisionalTranscripts({});
        applySession(authoritative);
        await attachMediaSafely(
          authoritative,
          lifecycleGeneration,
          preparedPlayback,
        );
        assertCurrentRequest();
      } catch (openError: unknown) {
        let publishedError = openError;
        if (
          preparedPlayback.prearmedContext &&
          preparedPlayback.prearmedContext.state !== 'closed'
        ) {
          try {
            await preparedPlayback.prearmedContext.close();
          } catch (contextCleanupError: unknown) {
            publishedError = contextCleanupError;
          }
        }
        if (
          ownershipGenerationRef.current === preparedPlayback.generation &&
          playbackRef.current === preparedPlayback.playback
        ) {
          try {
            await releaseMedia();
          } catch (cleanupError: unknown) {
            publishedError = cleanupError;
          }
        }
        const activeOpen = activeOpenAuthorityRef.current;
        if (
          activeOpen?.lifecycleGeneration === lifecycleGeneration &&
          activeOpen.openRequest === openRequest
        ) {
          const claim = sessionClaimRef.current;
          const hasCurrentClaim =
            claim?.lifecycleGeneration === lifecycleGeneration &&
            claim.openRequest === openRequest;
          if (!hasCurrentClaim) activeOpenAuthorityRef.current = null;
          flushDeferredStaleSessions(hasCurrentClaim ? claim.key : null);
        }
        const failedSessionIsCurrent = Boolean(
          attachingSession &&
            isCurrentAttachTopology(sessionRef.current, attachingSession),
        );
        const controlledOpeningCancellation = Boolean(
          attachingSession &&
            sameMediaAuthority(
              mediaAuthority(attachingSession),
              gatedOpeningAuthorityRef.current,
            ),
        );
        if (
          attachingSession &&
          failedSessionIsCurrent &&
          !controlledOpeningCancellation
        ) {
          activeOpenAuthorityRef.current = null;
          sessionClaimRef.current = null;
          sessionRef.current = null;
          setSession(null);
          setModelState(null);
          setProvisionalTranscripts({});
          mediaReadyRef.current = false;
          try {
            await remoteAuthorityRegistry.endAll(
              remoteAuthorityScope(attachingSession),
              [attachingSession],
              dependencies.randomUUID,
            );
          } catch (_cleanupError: unknown) {}
        }
        if (
          !controlledOpeningCancellation &&
          isLifecycleCurrent(lifecycleGeneration) &&
          latestOpenRequestRef.current === openRequest &&
          !(
            attachingSession &&
            !isCurrentAttachTopology(sessionRef.current, attachingSession)
          )
        ) {
          setStatus('error');
          setError(
            publishedError instanceof Error
              ? publishedError.message
              : 'Could not open voice',
          );
        }
        throw publishedError;
      }
    },
    [
      applySession,
      attachMediaSafely,
      defaultApi,
      dependencies,
      disposeStaleSession,
      flushDeferredStaleSessions,
      isLifecycleCurrent,
      reconcileDeferredStart,
      releaseMedia,
      remoteAuthorityRegistry,
    ],
  );

  recoverColdRuntimeRef.current = async (
    recoverySocket: VoiceSocketLike,
    recoveryGeneration: number,
  ): Promise<void> => {
    const current = sessionRef.current;
    if (
      !current ||
      socketRef.current !== recoverySocket ||
      ownershipGenerationRef.current !== recoveryGeneration ||
      (current.state !== 'active' && current.state !== 'reconnecting')
    ) {
      return;
    }
    if (current.transport.type === 'webrtc') {
      const lifecycleGeneration = lifecycleGenerationRef.current;
      const currentMediaAuthority = requireMediaAuthority(current);
      await suspendMedia();
      if (!isLifecycleCurrent(lifecycleGeneration)) return;
      const replacementRequest: VoiceSessionCommandRequest = {
        commandId: dependencies.randomUUID(),
        leaseVersion: current.leaseVersion,
        type: 'replace_media',
        mediaBindingId: currentMediaAuthority.mediaBindingId,
        mediaGeneration: currentMediaAuthority.mediaGeneration,
      };
      const recovered = await command(
        current,
        replacementRequest,
        lifecycleGeneration,
        false,
      );
      if (!isLifecycleCurrent(lifecycleGeneration)) return;
      const recoveredMediaAuthority = mediaAuthority(recovered);
      if (
        !hasActiveLease(recovered) ||
        recovered.sessionId !== current.sessionId ||
        recovered.teamId !== current.teamId ||
        recovered.conversationId !== current.conversationId ||
        recovered.userId !== current.userId ||
        recovered.currentAgentId !== current.currentAgentId ||
        !recoveredMediaAuthority ||
        recoveredMediaAuthority.mediaGeneration <=
          currentMediaAuthority.mediaGeneration ||
        recoveredMediaAuthority.mediaBindingId ===
          currentMediaAuthority.mediaBindingId
      ) {
        endUnclaimedSession(recovered);
        throw new Error('Recovered voice media authority changed; retry');
      }
      applySession(recovered, 'remote', recoverySocket);
      await topologyTransitionCompletionRef.current;
      if (
        isLifecycleCurrent(lifecycleGeneration) &&
        sameMediaBindingGeneration(
          mediaAuthority(sessionRef.current),
          recoveredMediaAuthority,
        )
      ) {
        setError(null);
        setStatus('active');
      }
      return;
    }
    const lifecycleGeneration = ++lifecycleGenerationRef.current;
    const openRequest = ++latestOpenRequestRef.current;
    const authority: VoiceStartAuthority = {
      teamId: current.teamId,
      conversationId: current.conversationId,
      agentId: current.currentAgentId,
    };
    activeOpenAuthorityRef.current = {
      ...authority,
      lifecycleGeneration,
      openRequest,
    };
    sessionClaimRef.current = null;
    await releaseMedia();
    if (!isLifecycleCurrent(lifecycleGeneration)) return;
    setStatus('reconnecting');
    let recovered: VoiceSessionSnapshot | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await defaultApi.startVoiceSession(
          authority.teamId,
          authority.conversationId,
          {
            commandId: dependencies.randomUUID(),
            agentId: authority.agentId,
          },
        );
        recovered = response.data;
        break;
      } catch (recoveryError: unknown) {
        if (
          attempt === 0 &&
          voiceRequestErrorCode(recoveryError) ===
            'voice_runtime_expired_converged'
        ) {
          continue;
        }
        throw recoveryError;
      }
    }
    if (!recovered) throw new Error('Voice session recovery failed');
    if (!isLifecycleCurrent(lifecycleGeneration)) {
      disposeStaleSession(recovered, lifecycleGeneration, {
        kind: 'start',
        authority,
      });
      return;
    }
    if (
      !hasActiveLease(recovered) ||
      !sessionMatchesStartAuthority(recovered, authority)
    ) {
      endUnclaimedSession(recovered);
      throw new Error('Recovered voice session authority changed; retry');
    }
    applySession(recovered, 'local');
    await attachMediaSafely(recovered, lifecycleGeneration);
    if (
      isLifecycleCurrent(lifecycleGeneration) &&
      isCurrentAttachTopology(sessionRef.current, recovered)
    ) {
      setStatus('active');
    }
  };

  const ownsConversation = useCallback(
    (conversationId?: number): boolean =>
      conversationId == null || conversationRef.current === conversationId,
    [],
  );

  const pause = useCallback(
    async (
      conversationId?: number,
      lifecycleGeneration = lifecycleGenerationRef.current,
    ): Promise<void> => {
      if (
        !isLifecycleCurrent(lifecycleGeneration) ||
        !ownsConversation(conversationId)
      )
        return;
      await pauseCurrent(true, lifecycleGeneration);
    },
    [isLifecycleCurrent, ownsConversation, pauseCurrent],
  );

  const resume = useCallback(
    async (
      conversationId?: number,
      lifecycleGeneration = lifecycleGenerationRef.current,
    ): Promise<void> => {
      if (
        !isLifecycleCurrent(lifecycleGeneration) ||
        !ownsConversation(conversationId)
      )
        return;
      const activation = (
        navigator as Navigator & { userActivation?: { isActive: boolean } }
      ).userActivation;
      if (!activation?.isActive) {
        throw new Error('Resume voice from a user gesture');
      }
      const current = sessionRef.current;
      if (!current || current.state !== 'paused') return;
      const retainedTransport = openAITransportRef.current;
      if (current.transport.type === 'webrtc') {
        if (!retainedTransport) {
          throw new Error('OpenAI Realtime media must be restarted');
        }
        const generation = ownershipGenerationRef.current;
        const resumeAuthorityIsCurrent = (): boolean => {
          const authoritative = sessionRef.current;
          return (
            isLifecycleCurrent(lifecycleGeneration) &&
            ownershipGenerationRef.current === generation &&
            openAITransportRef.current === retainedTransport &&
            conversationRef.current === current.conversationId &&
            authoritative?.sessionId === current.sessionId &&
            authoritative.conversationId === current.conversationId
          );
        };
        const devices = readDevices(dependencies.storage);
        const getUserMedia =
          dependencies.getUserMedia ??
          ((constraints: MediaStreamConstraints) =>
            navigator.mediaDevices.getUserMedia(constraints));
        let replacementStream: MediaStream;
        try {
          replacementStream = await getUserMedia({
            audio: realtimeMicrophoneConstraints(devices.input),
            video: false,
          });
        } catch (deviceError: unknown) {
          const invalidSavedDevice =
            devices.input &&
            deviceError instanceof DOMException &&
            ['NotFoundError', 'OverconstrainedError'].includes(
              deviceError.name,
            );
          if (!invalidSavedDevice) throw deviceError;
          if (!resumeAuthorityIsCurrent()) {
            throw new Error('Voice channel ownership was disposed');
          }
          writeDevices(dependencies.storage, {
            ...devices,
            input: undefined,
          });
          replacementStream = await getUserMedia({
            audio: realtimeMicrophoneConstraints(),
            video: false,
          });
        }
        let resumedSession: VoiceSessionSnapshot | null = null;
        let streamTransferred = false;
        try {
          if (!resumeAuthorityIsCurrent()) {
            for (const track of replacementStream.getTracks()) track.stop();
            throw new Error('Voice channel ownership was disposed');
          }
          const next = await command(
            current,
            {
              commandId: dependencies.randomUUID(),
              leaseVersion: current.leaseVersion,
              type: 'resume',
            },
            lifecycleGeneration,
            false,
          );
          if (!resumeAuthorityIsCurrent()) {
            for (const track of replacementStream.getTracks()) track.stop();
            return;
          }
          if (!hasActiveLease(next)) {
            for (const track of replacementStream.getTracks()) track.stop();
            applySession(
              next,
              next.state === 'paused' || next.state === 'ended'
                ? 'remote'
                : 'local',
            );
            await suspendMedia();
            if (resumeAuthorityIsCurrent()) {
              setStatus('error');
              setError('Voice session is not active; retry');
            }
            return;
          }
          const currentMediaAuthority = requireMediaAuthority(current);
          const nextMediaAuthority = mediaAuthority(next);
          const warmResume = sameSessionMediaBindingGeneration(
            currentMediaAuthority,
            nextMediaAuthority,
          );
          const coldResume = isNewMediaBindingGeneration(
            currentMediaAuthority,
            nextMediaAuthority,
          );
          if (
            next.teamId !== current.teamId ||
            next.conversationId !== current.conversationId ||
            next.userId !== current.userId ||
            next.currentAgentId !== current.currentAgentId ||
            (!warmResume && !coldResume)
          ) {
            endUnclaimedSession(next);
            throw new Error('Voice resume authority changed; retry');
          }
          resumedSession = next;
          if (coldResume) {
            await releaseMedia();
            if (!isLifecycleCurrent(lifecycleGeneration)) {
              for (const track of replacementStream.getTracks()) track.stop();
              return;
            }
            applySession(next, 'local');
            streamTransferred = true;
            await attachMediaSafely(
              next,
              lifecycleGeneration,
              undefined,
              replacementStream,
            );
            return;
          }
          applySession(next, 'local');
          const replacementMediaWarning =
            hardenRealtimeMicrophoneStream(replacementStream);
          await retainedTransport.resume({
            userGestureActive: true,
            stream: replacementStream,
          });
          streamTransferred = true;
          if (!resumeAuthorityIsCurrent()) {
            await retainedTransport.close();
            return;
          }
          openAITransportPausedRef.current = false;
          mediaAttachedRef.current = true;
          setMediaWarning(replacementMediaWarning);
          attachSocket(next, generation);
          publishMediaReady(next, generation);
          return;
        } catch (resumeError: unknown) {
          if (!streamTransferred) {
            for (const track of replacementStream.getTracks()) {
              if (track.readyState !== 'ended') track.stop();
            }
          }
          let rollbackFailed = false;
          const authoritative = sessionRef.current;
          const resumedSessionIsCurrent = Boolean(
            resumedSession &&
            authoritative?.sessionId === resumedSession.sessionId &&
            authoritative.leaseVersion === resumedSession.leaseVersion,
          );
          if (
            resumedSession &&
            hasActiveLease(resumedSession) &&
            (resumeAuthorityIsCurrent() || resumedSessionIsCurrent)
          ) {
            try {
              await command(
                resumedSession,
                {
                  commandId: dependencies.randomUUID(),
                  leaseVersion: resumedSession.leaseVersion,
                  type: 'pause',
                },
                lifecycleGeneration,
              );
            } catch (_rollbackError: unknown) {
              rollbackFailed = true;
              endCriticalSession(resumedSession);
            }
          }
          if (!resumeAuthorityIsCurrent()) {
            if (rollbackFailed && resumedSession) {
              endCriticalSession(resumedSession);
            }
            throw resumeError;
          }
          if (rollbackFailed && resumedSession) {
            const blocker = new Error(
              'Voice resume rollback could not be confirmed; ending the session',
            );
            activeOpenAuthorityRef.current = null;
            sessionClaimRef.current = null;
            sessionRef.current = null;
            setSession(null);
            setModelState(null);
            setProvisionalTranscripts({});
            endCriticalSession(resumedSession);
            setStatus('error');
            setError(blocker.message);
            try {
              await releaseMedia();
            } catch (_releaseError: unknown) {
              void _releaseError;
            }
            throw blocker;
          }
          if (resumeAuthorityIsCurrent()) {
            await suspendMedia();
          }
          throw resumeError;
        }
      }
      const generation = ++ownershipGenerationRef.current;
      const outputElement = dependencies.createAudioElement?.() ?? new Audio();
      const playback = createPlayback(generation, outputElement);
      try {
        const outputDeviceId = readDevices(dependencies.storage).output;
        try {
          await playback.resume(outputDeviceId);
        } catch (deviceError: unknown) {
          if (!outputDeviceId) throw deviceError;
          const devices = readDevices(dependencies.storage);
          writeDevices(dependencies.storage, { ...devices, output: undefined });
          await playback.resume();
        }
        if (
          !isLifecycleCurrent(lifecycleGeneration) ||
          ownershipGenerationRef.current !== generation
        ) {
          throw new Error('Voice channel ownership was disposed');
        }
        const next = await command(
          current,
          {
            commandId: dependencies.randomUUID(),
            leaseVersion: current.leaseVersion,
            type: 'resume',
          },
          lifecycleGeneration,
        );
        if (!hasActiveLease(next)) {
          if (ownershipGenerationRef.current === generation) {
            await releaseMedia();
          }
          const authoritative = sessionRef.current;
          if (
            isLifecycleCurrent(lifecycleGeneration) &&
            authoritative?.sessionId === next.sessionId &&
            authoritative.leaseVersion === next.leaseVersion &&
            next.state !== 'paused' &&
            next.state !== 'ended'
          ) {
            setStatus('error');
            setError('Voice session is not active; retry');
          }
          return;
        }
        if (
          !isLifecycleCurrent(lifecycleGeneration) ||
          ownershipGenerationRef.current !== generation
        )
          return;
        await attachMedia(next, generation, {
          playback,
          generation,
          ready: Promise.resolve(),
          outputElement,
        });
        mediaAttachedRef.current = true;
        publishMediaReady(next, generation);
      } catch (resumeError: unknown) {
        if (ownershipGenerationRef.current === generation) {
          await releaseMedia();
        }
        throw resumeError;
      }
    },
    [
      attachMedia,
      attachMediaSafely,
      attachSocket,
      applySession,
      command,
      createPlayback,
      dependencies,
      endUnclaimedSession,
      endCriticalSession,
      isLifecycleCurrent,
      ownsConversation,
      publishMediaReady,
      releaseMedia,
      suspendMedia,
    ],
  );

  const switchAgent = useCallback(
    async (
      agentId: number,
      conversationId?: number,
      lifecycleGeneration = lifecycleGenerationRef.current,
      gatedOpeningAuthority?: VoiceMediaAuthority | null,
    ): Promise<void> => {
      if (
        !isLifecycleCurrent(lifecycleGeneration) ||
        !ownsConversation(conversationId)
      )
        return;
      const cleanupBlocker = criticalCleanupBlocker();
      if (cleanupBlocker) throw cleanupBlocker;
      const current = sessionRef.current;
      if (!current) throw new Error('Voice session is unavailable');
      if (!hasActiveLease(current)) {
        throw new Error('Voice session is not active; retry');
      }
      if (pttTurnRef.current) await cancelPtt();
      if (!isLifecycleCurrent(lifecycleGeneration)) return;
      const switchingTransport = openAITransportRef.current;
      if (switchingTransport && !openAITransportPausedRef.current) {
        openAITransportPausedRef.current = true;
        clearInputAnalysis();
        clearOutputAnalysis();
        await switchingTransport.pause();
        if (
          !isLifecycleCurrent(lifecycleGeneration) ||
          openAITransportRef.current !== switchingTransport
        ) {
          return;
        }
      }
      const next = await command(
        current,
        {
          commandId: dependencies.randomUUID(),
          leaseVersion: current.leaseVersion,
          type: 'switch_agent',
          agentId,
        },
        lifecycleGeneration,
        false,
      );
      if (!isLifecycleCurrent(lifecycleGeneration)) return;
      const socketDrivenSwitch = sessionRef.current;
      if (
        topologyTransitionRef.current &&
        socketDrivenSwitch &&
        socketDrivenSwitch.sessionId === next.sessionId &&
        socketDrivenSwitch.currentAgentId === agentId &&
        socketDrivenSwitch.leaseVersion === next.leaseVersion &&
        sameMediaTopology(socketDrivenSwitch, next)
      ) {
        await topologyTransitionCompletionRef.current;
        if (!isLifecycleCurrent(lifecycleGeneration)) return;
      }
      const authoritative = sessionRef.current;
      const validSwitchMediaAuthority =
        current.transport.type !== 'webrtc' ||
        isNewMediaBindingGeneration(
          mediaAuthority(current),
          mediaAuthority(next),
        );
      const authoritativeOwnsSwitchMedia = Boolean(
        authoritative &&
        (playbackRef.current || openAITransportRef.current) &&
        socketRef.current &&
        (hasActiveLease(authoritative) ||
          authoritative.state === 'reconnecting'),
      );
      const canRecoverGatedOpening = Boolean(
        !authoritativeOwnsSwitchMedia &&
        authoritative &&
        gatedOpeningAuthority &&
        hasActiveLease(authoritative) &&
        sameMediaAuthority(
          mediaAuthority(authoritative),
          gatedOpeningAuthority,
        ),
      );
      if (
        !authoritative ||
        (!authoritativeOwnsSwitchMedia && !canRecoverGatedOpening) ||
        !validSwitchMediaAuthority ||
        !sessionMatchesSwitchAuthority(authoritative, next, agentId)
      ) {
        const abandonsCurrentAuthority =
          authoritative != null &&
          next.sessionId === authoritative.sessionId &&
          next.leaseVersion >= authoritative.leaseVersion;
        if (abandonsCurrentAuthority) {
          activeOpenAuthorityRef.current = null;
          sessionClaimRef.current = null;
          flushDeferredStaleSessions(null);
          endUnclaimedSession(next);
          sessionRef.current = null;
          setSession(null);
          setModelState(null);
          setProvisionalTranscripts({});
          await releaseMedia();
        } else {
          endUnclaimedSession(next);
        }
        setStatus('error');
        setError('Voice switch authority changed; retry');
        throw new Error('Voice switch authority changed; retry');
      }
      if (!hasActiveLease(next)) {
        applySession(
          next,
          next.state === 'paused' || next.state === 'ended'
            ? 'remote'
            : 'local',
        );
        if (next.state !== 'paused' && next.state !== 'ended') {
          await releaseMedia();
          setStatus('error');
          setError('Voice session is not active; retry');
        }
        throw new Error('Voice session is not active; retry');
      }
      if (canRecoverGatedOpening) {
        applySession(next, 'local');
        await attachMediaSafely(next, lifecycleGeneration);
        return;
      }
      applySession(next, 'remote', socketRef.current ?? undefined);
      let transition = topologyTransitionCompletionRef.current;
      while (isLifecycleCurrent(lifecycleGeneration)) {
        try {
          await transition;
        } catch (transitionError: unknown) {
          if (!isLifecycleCurrent(lifecycleGeneration)) return;
          const successor = topologyTransitionCompletionRef.current;
          if (successor !== transition) {
            transition = successor;
            continue;
          }
          throw transitionError;
        }
        const successor = topologyTransitionCompletionRef.current;
        if (!topologyTransitionRef.current || successor === transition) break;
        transition = successor;
      }
    },
    [
      applySession,
      attachMediaSafely,
      cancelPtt,
      command,
      criticalCleanupBlocker,
      dependencies,
      endUnclaimedSession,
      flushDeferredStaleSessions,
      isLifecycleCurrent,
      ownsConversation,
      releaseMedia,
    ],
  );

  const beginPtt = useCallback(
    async (
      conversationId?: number,
      lifecycleGeneration = lifecycleGenerationRef.current,
    ): Promise<void> => {
      if (
        !isLifecycleCurrent(lifecycleGeneration) ||
        !ownsConversation(conversationId)
      )
        return;
      const current = sessionRef.current;
      if (current?.profileSnapshot.profile.mode !== 'push_to_talk') return;
      if (recorderRef.current || pttTurnRef.current) return;
      const socket = socketRef.current;
      if (!socket) throw new Error('Voice socket is unavailable');
      const generation = ownershipGenerationRef.current;
      playbackRef.current?.interrupt();
      clearOutputAnalysis();
      const turnKey = dependencies.randomUUID();
      pttTurnRef.current = turnKey;
      // This interruption stops any response that was already playing. The
      // freshly allocated PTT turn does not exist server-side until commit,
      // so tagging it here would pre-mark the new turn as interrupted/silent.
      socket.interrupt();
      const recorder = createRecorder(socket, generation);
      try {
        await recorder.start(readDevices(dependencies.storage).input);
        if (
          !isLifecycleCurrent(lifecycleGeneration) ||
          ownershipGenerationRef.current !== generation ||
          recorderRef.current !== recorder
        )
          throw new Error('Voice channel ownership was disposed');
      } catch (startError: unknown) {
        const ownedRecorder = recorderRef.current as RecorderLike | null;
        if (ownedRecorder === recorder) {
          recorderRef.current = null;
          pttTurnRef.current = null;
          await ownedRecorder.stop();
        }
        throw startError;
      }
    },
    [createRecorder, dependencies, isLifecycleCurrent, ownsConversation],
  );

  const commitPtt = useCallback(
    async (
      conversationId?: number,
      lifecycleGeneration = lifecycleGenerationRef.current,
    ): Promise<void> => {
      if (
        !isLifecycleCurrent(lifecycleGeneration) ||
        !ownsConversation(conversationId)
      )
        return;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder) await recorder.stop();
      clearInputAnalysis();
      const turnKey = pttTurnRef.current;
      pttTurnRef.current = null;
      if (turnKey && isLifecycleCurrent(lifecycleGeneration)) {
        socketRef.current?.commitInput(turnKey);
      }
    },
    [isLifecycleCurrent, ownsConversation],
  );

  const interrupt = useCallback(
    async (
      conversationId?: number,
      lifecycleGeneration = lifecycleGenerationRef.current,
    ): Promise<void> => {
      if (
        !isLifecycleCurrent(lifecycleGeneration) ||
        !ownsConversation(conversationId)
      )
        return;
      playbackRef.current?.interrupt();
      openAITransportRef.current?.interrupt();
      clearOutputAnalysis();
      socketRef.current?.interrupt(pttTurnRef.current || undefined);
    },
    [isLifecycleCurrent, ownsConversation],
  );

  const end = useCallback(
    async (conversationId?: number): Promise<void> => {
      if (!ownsConversation(conversationId)) return;
      const lifecycleGeneration = ++lifecycleGenerationRef.current;
      latestOpenRequestRef.current += 1;
      operationRef.current = Promise.resolve();
      const current = sessionRef.current;
      const endedConversationId =
        conversationRef.current ?? current?.conversationId ?? null;
      const targetConversationId = conversationId ?? endedConversationId;
      const deferredAuthorities: VoiceSessionSnapshot[] = [];
      for (const [key, candidate] of deferredStaleSessionsRef.current) {
        if (
          targetConversationId == null ||
          candidate.session.conversationId === targetConversationId
        ) {
          deferredAuthorities.push(candidate.session);
          deferredStaleSessionsRef.current.delete(key);
        }
      }
      const authorityCandidates = [
        ...(current ? [current] : []),
        ...deferredAuthorities,
      ];
      const scopes = new Map<string, RemoteVoiceAuthorityScope>();
      for (const candidate of authorityCandidates) {
        const scope = remoteAuthorityScope(candidate);
        scopes.set(remoteAuthorityScopeKey(scope), scope);
      }
      for (const scope of remoteAuthorityRegistry.scopes(
        targetConversationId ?? undefined,
      )) {
        scopes.set(remoteAuthorityScopeKey(scope), scope);
      }
      activeOpenAuthorityRef.current = null;
      sessionClaimRef.current = null;
      const remote = Promise.all(
        [...scopes.values()].map((scope) =>
          remoteAuthorityRegistry.endAll(
            scope,
            authorityCandidates,
            dependencies.randomUUID,
          ),
        ),
      );
      const terminalSocket = socketRef.current;
      socketRef.current = null;
      const local = releaseMedia();
      const releaseTerminalSocket = async (): Promise<void> => {
        if (terminalSocket) await terminalSocket.end();
      };
      const terminalSocketRelease = remote.then(
        releaseTerminalSocket,
        releaseTerminalSocket,
      );
      sessionRef.current = null;
      conversationRef.current = endedConversationId;
      setActiveConversationId(endedConversationId);
      setSession(null);
      setModelState(null);
      setProvisionalTranscripts({});
      setMediaWarning(null);
      setError(null);
      setStatus('ended');
      explicitEndCleanupGenerationRef.current =
        scopes.size > 0 ? lifecycleGeneration : null;
      const [remoteResult, localResult, terminalSocketResult] =
        await Promise.allSettled([remote, local, terminalSocketRelease]);
      if (localResult.status === 'rejected') {
        const message =
          localResult.reason instanceof Error
            ? localResult.reason.message
            : 'Could not release voice media';
        if (isLifecycleCurrent(lifecycleGeneration)) setError(message);
        throw localResult.reason;
      }
      if (terminalSocketResult.status === 'rejected') {
        const message =
          terminalSocketResult.reason instanceof Error
            ? terminalSocketResult.reason.message
            : 'Could not release voice media';
        if (isLifecycleCurrent(lifecycleGeneration)) setError(message);
        throw terminalSocketResult.reason;
      }
      if (remoteResult.status === 'rejected') {
        if (isLifecycleCurrent(lifecycleGeneration)) {
          setStatus('error');
          setError(FINISHING_PREVIOUS_SESSION_MESSAGE);
        }
        throw remoteResult.reason;
      }
      if (explicitEndCleanupGenerationRef.current === lifecycleGeneration) {
        explicitEndCleanupGenerationRef.current = null;
      }
      if (isLifecycleCurrent(lifecycleGeneration)) {
        setError(null);
        setStatus('ended');
      }
    },
    [
      dependencies,
      isLifecycleCurrent,
      ownsConversation,
      releaseMedia,
      remoteAuthorityRegistry,
    ],
  );
  const returnToText = useCallback(
    async (conversationId?: number): Promise<void> => {
      if (!ownsConversation(conversationId)) return;
      const targetConversationId =
        conversationId ??
        conversationRef.current ??
        sessionRef.current?.conversationId;
      const pendingOperation = operationRef.current;
      await end(conversationId);
      await pendingOperation.catch(() => undefined);
      await Promise.all(
        remoteAuthorityRegistry
          .scopes(targetConversationId)
          .map((scope) =>
            remoteAuthorityRegistry.endAll(scope, [], dependencies.randomUUID),
          ),
      );
    },
    [dependencies, end, ownsConversation, remoteAuthorityRegistry],
  );

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      activeOpenAuthorityRef.current = null;
      sessionClaimRef.current = null;
      flushDeferredStaleSessions(null);
      void releaseMedia(false).catch(() => undefined);
    };
  }, [flushDeferredStaleSessions, releaseMedia]);

  const serializedOpen = useCallback(
    (conversationId: number, input: OpenVoiceChannelInput) => {
      if (
        remoteAuthorityRegistry.hasUncertainty() &&
        explicitEndCleanupGenerationRef.current !== null
      ) {
        const cleanupLifecycleGeneration = ++lifecycleGenerationRef.current;
        const cleanupOpenRequest = ++latestOpenRequestRef.current;
        operationRef.current = Promise.resolve();
        setStatus('opening');
        setError(null);
        return retryCriticalCleanup().then(
          () => {
            if (
              !isLifecycleCurrent(cleanupLifecycleGeneration) ||
              latestOpenRequestRef.current !== cleanupOpenRequest
            ) {
              throw new Error('Voice channel ownership was disposed');
            }
            return retryOpenRef.current(conversationId, input);
          },
          (cleanupError: unknown) => {
            if (isLifecycleCurrent(cleanupLifecycleGeneration)) {
              setStatus('error');
              setError(FINISHING_PREVIOUS_SESSION_MESSAGE);
            }
            throw cleanupError;
          },
        );
      }
      const cleanupBlocker = criticalCleanupBlocker();
      if (cleanupBlocker) {
        if (!remoteAuthorityRegistry.hasOnlyExhaustedUncertainty()) {
          return Promise.reject(cleanupBlocker);
        }
        const cleanupLifecycleGeneration = ++lifecycleGenerationRef.current;
        const cleanupOpenRequest = ++latestOpenRequestRef.current;
        operationRef.current = Promise.resolve();
        return retryCriticalCleanup().then(
          () => {
            if (
              !isLifecycleCurrent(cleanupLifecycleGeneration) ||
              latestOpenRequestRef.current !== cleanupOpenRequest
            ) {
              throw new Error('Voice channel ownership was disposed');
            }
            return retryOpenRef.current(conversationId, input);
          },
          (cleanupError: unknown) => {
            if (isLifecycleCurrent(cleanupLifecycleGeneration)) {
              setStatus('error');
              setError(FINISHING_PREVIOUS_SESSION_MESSAGE);
            }
            throw cleanupError;
          },
        );
      }
      const previous = sessionRef.current;
      const previousConversationId =
        conversationRef.current ?? previous?.conversationId ?? null;
      const lifecycleGeneration = ++lifecycleGenerationRef.current;
      const openRequest = ++latestOpenRequestRef.current;
      activeOpenAuthorityRef.current = {
        lifecycleGeneration,
        openRequest,
        teamId: input.teamId,
        conversationId,
        agentId: input.agentId,
      };
      sessionClaimRef.current = null;
      operationRef.current = Promise.resolve();
      const fulfilledRelease: PromiseSettledResult<void> = {
        status: 'fulfilled',
        value: undefined,
      };
      const settleRelease = (
        attempt: () => unknown,
      ): Promise<PromiseSettledResult<void>> => {
        try {
          return Promise.resolve(attempt()).then(
            () => ({ status: 'fulfilled', value: undefined }),
            (reason: unknown) => ({ status: 'rejected', reason }),
          );
        } catch (reason: unknown) {
          return Promise.resolve({ status: 'rejected', reason });
        }
      };
      const supersededAllocationRelease = settleRelease(() =>
        disposeOpeningAllocation(),
      );
      let cancelAllocation = (): void => undefined;
      const cancelled = new Promise<void>((resolve) => {
        cancelAllocation = resolve;
      });
      const allocation: VoiceOpenAllocation = {
        lifecycleGeneration,
        openRequest,
        localRelease: Promise.resolve(fulfilledRelease),
        remoteRelease: Promise.resolve(fulfilledRelease),
        outputElement: null,
        preparedPlayback: null,
        cancelled,
        cancel: cancelAllocation,
        disposed: false,
      };
      openingAllocationRef.current = allocation;
      const currentMediaRelease = settleRelease(() =>
        releaseMedia(false, false, allocation),
      );
      allocation.localRelease = Promise.all([
        supersededAllocationRelease,
        currentMediaRelease,
      ]).then(
        (results) =>
          results.find(
            (result): result is PromiseRejectedResult =>
              result.status === 'rejected',
          ) ?? fulfilledRelease,
      );
      allocation.remoteRelease = settleRelease(() =>
        previous && previous.state !== 'ended'
          ? remoteAuthorityRegistry.execute(previous, {
              commandId: dependencies.randomUUID(),
              leaseVersion: previous.leaseVersion,
              type: previousConversationId === conversationId ? 'pause' : 'end',
            })
          : undefined,
      );
      let allocationResult: { ok: true } | { ok: false; error: unknown } = {
        ok: true,
      };
      try {
        const preparedOutputDeviceId =
          input.outputDeviceId ?? readDevices(dependencies.storage).output;
        const preparedOutputElement =
          dependencies.createAudioElement?.() ?? new Audio();
        allocation.outputElement = preparedOutputElement;
        writeDevices(dependencies.storage, {
          input: input.inputDeviceId,
          output: input.outputDeviceId,
        });
        allocation.preparedPlayback = preparePlayback(
          preparedOutputDeviceId,
          preparedOutputElement,
          input.inputDeviceId,
        );
      } catch (error: unknown) {
        allocationResult = { ok: false, error };
      }
      const finalizeAllocation = <Result,>(
        result: Promise<Result>,
      ): Promise<Result> =>
        result.then(
          (value) => {
            if (openingAllocationRef.current === allocation) {
              openingAllocationRef.current = null;
            }
            return value;
          },
          (error: unknown) => {
            if (openingAllocationRef.current === allocation) {
              openingAllocationRef.current = null;
            }
            throw error;
          },
        );
      if (!allocationResult.ok) {
        const allocationError = allocationResult.error;
        const allocationCleanup = settleRelease(() =>
          disposeOpeningAllocation(),
        );
        return finalizeAllocation(
          serialize(async () => {
            await allocationCleanup;
            if (
              isLifecycleCurrent(lifecycleGeneration) &&
              latestOpenRequestRef.current === openRequest
            ) {
              activeOpenAuthorityRef.current = null;
              sessionClaimRef.current = null;
              flushDeferredStaleSessions(null);
              sessionRef.current = null;
              conversationRef.current = conversationId;
              setActiveConversationId(conversationId);
              setSession(null);
              setModelState(null);
              clearInputAnalysis();
              clearOutputAnalysis();
              setProvisionalTranscripts({});
              setStatus('error');
              setError(
                allocationError instanceof Error
                  ? allocationError.message
                  : 'Could not allocate voice media',
              );
            }
            throw allocationError;
          }),
        );
      }
      const preparedPlayback = allocation.preparedPlayback;
      if (!preparedPlayback) {
        return finalizeAllocation(
          Promise.reject(
            new Error('Voice playback allocation was unavailable'),
          ),
        );
      }
      const predecessorRelease = Promise.all([
        allocation.localRelease,
        allocation.remoteRelease,
      ]).then((results) => {
        const failure = results.find(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected',
        );
        if (failure) throw failure.reason;
      });
      const boundedPredecessorRelease = Promise.race([
        predecessorRelease,
        allocation.cancelled.then(() => {
          throw new Error('Voice opening was cancelled');
        }),
      ]);
      sessionRef.current = null;
      conversationRef.current = conversationId;
      setActiveConversationId(conversationId);
      setSession(null);
      setStatus('opening');
      setModelState(null);
      clearInputAnalysis();
      clearOutputAnalysis();
      setProvisionalTranscripts({});
      setMediaWarning(null);
      setError(null);
      return finalizeAllocation(
        serialize(() =>
          openForConversation(
            conversationId,
            input,
            lifecycleGeneration,
            openRequest,
            boundedPredecessorRelease,
            preparedPlayback,
          ),
        ),
      );
    },
    [
      criticalCleanupBlocker,
      dependencies,
      disposeOpeningAllocation,
      flushDeferredStaleSessions,
      isLifecycleCurrent,
      openForConversation,
      preparePlayback,
      releaseMedia,
      remoteAuthorityRegistry,
      retryCriticalCleanup,
      serialize,
    ],
  );
  retryOpenRef.current = serializedOpen;
  const serializedPause = useCallback(
    (conversationId?: number) => {
      if (!ownsConversation(conversationId)) return Promise.resolve();
      if (!mediaReadyRef.current && !topologyTransitionRef.current) {
        return Promise.resolve();
      }
      const lifecycleGeneration = lifecycleGenerationRef.current;
      const gated = gateOpenAIRealtimeMedia();
      return serialize(async () => {
        try {
          await gated;
          await pause(conversationId, lifecycleGeneration);
        } catch (pauseError: unknown) {
          const failedSession = sessionRef.current;
          if (
            failedSession &&
            isLifecycleCurrent(lifecycleGeneration) &&
            ownsConversation(conversationId)
          ) {
            activeOpenAuthorityRef.current = null;
            sessionClaimRef.current = null;
            flushDeferredStaleSessions(null);
            sessionRef.current = null;
            setSession(null);
            setModelState(null);
            setProvisionalTranscripts({});
            setMediaWarning(null);
            const cleanup = await Promise.allSettled([
              releaseMedia(),
              remoteAuthorityRegistry.endAll(
                remoteAuthorityScope(failedSession),
                [failedSession],
                dependencies.randomUUID,
              ),
            ]);
            if (
              isLifecycleCurrent(lifecycleGeneration) &&
              sessionRef.current === null
            ) {
              const cleanupFailure = cleanup.find(
                (result): result is PromiseRejectedResult =>
                  result.status === 'rejected',
              );
              const publishedError = cleanupFailure?.reason ?? pauseError;
              setStatus('error');
              setError(
                publishedError instanceof Error
                  ? publishedError.message
                  : 'Could not mute voice chat',
              );
            }
          }
          throw pauseError;
        }
      });
    },
    [
      dependencies,
      flushDeferredStaleSessions,
      gateOpenAIRealtimeMedia,
      isLifecycleCurrent,
      ownsConversation,
      pause,
      releaseMedia,
      remoteAuthorityRegistry,
      serialize,
    ],
  );
  const serializedResume = useCallback(
    (conversationId?: number) => {
      const lifecycleGeneration = lifecycleGenerationRef.current;
      return serialize(() => resume(conversationId, lifecycleGeneration));
    },
    [resume, serialize],
  );
  const serializedSwitchAgent = useCallback(
    (agentId: number, conversationId?: number) => {
      if (!ownsConversation(conversationId)) return Promise.resolve();
      const lifecycleGeneration = lifecycleGenerationRef.current;
      const gatedOpeningAuthority = openAITransportRef.current
        ? mediaAuthority(sessionRef.current)
        : null;
      if (gatedOpeningAuthority && !mediaReadyRef.current) {
        gatedOpeningAuthorityRef.current = gatedOpeningAuthority;
      }
      const gated = gateOpenAIRealtimeMedia();
      return serialize(async () => {
        try {
          await gated;
          await switchAgent(
            agentId,
            conversationId,
            lifecycleGeneration,
            gatedOpeningAuthority,
          );
        } finally {
          if (
            sameMediaAuthority(
              gatedOpeningAuthorityRef.current,
              gatedOpeningAuthority,
            )
          ) {
            gatedOpeningAuthorityRef.current = null;
          }
        }
      });
    },
    [gateOpenAIRealtimeMedia, ownsConversation, serialize, switchAgent],
  );
  const serializedBeginPtt = useCallback(
    (conversationId?: number) => {
      const lifecycleGeneration = lifecycleGenerationRef.current;
      return serialize(() => beginPtt(conversationId, lifecycleGeneration));
    },
    [beginPtt, serialize],
  );
  const serializedCommitPtt = useCallback(
    (conversationId?: number) => {
      const lifecycleGeneration = lifecycleGenerationRef.current;
      return serialize(() => commitPtt(conversationId, lifecycleGeneration));
    },
    [commitPtt, serialize],
  );
  const serializedCancelPtt = useCallback(
    (conversationId?: number) => {
      const lifecycleGeneration = lifecycleGenerationRef.current;
      return serialize(async () => {
        if (
          !isLifecycleCurrent(lifecycleGeneration) ||
          !ownsConversation(conversationId)
        )
          return;
        await cancelPtt();
      });
    },
    [cancelPtt, isLifecycleCurrent, ownsConversation, serialize],
  );
  const serializedInterrupt = useCallback(
    (conversationId?: number) => {
      const lifecycleGeneration = lifecycleGenerationRef.current;
      return serialize(() => interrupt(conversationId, lifecycleGeneration));
    },
    [interrupt, serialize],
  );
  const serializedEnd = end;
  const serializedReturnToText = returnToText;

  const setMuted = useCallback((next: boolean) => {
    mutedRef.current = next;
    setMutedState(next);
    openAITransportRef.current?.setMuted?.(next);
  }, []);

  const sessionIdForInteractionReset = session?.sessionId ?? null;
  useEffect(() => {
    mutedRef.current = false;
    setMutedState(false);
    setBargeInAt(null);
    openAITransportRef.current?.setMuted?.(false);
  }, [sessionIdForInteractionReset]);

  const value = useMemo<VoiceChannelCoreValue>(
    () => ({
      activeConversationId,
      session,
      status,
      providerUsageObservationGeneration,
      modelState,
      muted,
      bargeInAt,
      setMuted,
      inputLevel,
      outputLevel,
      inputSpectrum,
      outputSpectrum,
      provisionalTranscripts,
      mediaWarning,
      error,
      openForConversation: serializedOpen,
      pause: serializedPause,
      resume: serializedResume,
      switchAgent: serializedSwitchAgent,
      beginPtt: serializedBeginPtt,
      commitPtt: serializedCommitPtt,
      cancelPtt: serializedCancelPtt,
      interrupt: serializedInterrupt,
      end: serializedEnd,
      returnToText: serializedReturnToText,
    }),
    [
      activeConversationId,
      error,
      inputLevel,
      inputSpectrum,
      mediaWarning,
      outputLevel,
      outputSpectrum,
      provisionalTranscripts,
      providerUsageObservationGeneration,
      serializedBeginPtt,
      serializedCancelPtt,
      serializedCommitPtt,
      serializedEnd,
      serializedInterrupt,
      serializedOpen,
      serializedPause,
      serializedResume,
      serializedReturnToText,
      serializedSwitchAgent,
      session,
      status,
      modelState,
      muted,
      bargeInAt,
      setMuted,
    ],
  );

  return (
    <VoiceChannelContext.Provider value={value}>
      {children}
    </VoiceChannelContext.Provider>
  );
};
